import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { headlineMessage, problemTone, providerLabel } from '../enrichmentProblems';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X as XPhosphor,
  PencilSimple,
  Trash,
  Export,
  BookOpen as BookOpenPhosphor,
  Target as TargetPhosphor,
  Lightning,
  Envelope,
  Clock as ClockPhosphor,
  Buildings,
  Briefcase as BriefcasePhosphor,
  MapPin as MapPinPhosphor,
  Phone as PhonePhosphor,
  LinkedinLogo,
  TwitterLogo,
  LinkSimple,
  ArrowSquareOut,
  ShieldCheck,
  CheckCircle,
  XCircle as XCirclePhosphor,
  Warning,
  CircleNotch,
  MagnifyingGlass,
  User,
  Globe,
  Sparkle,
  CurrencyDollar,
  TrendUp,
  FileText,
  ChartBar,
  Newspaper,
  UsersThree,
  CaretDown,
  Brain,
  EnvelopeOpen,
  CursorClick,
  ArrowBendUpLeft,
  PaperPlaneTilt,
  Gauge,
} from '@phosphor-icons/react';
import { Button } from '../../../components/ui/Button';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog';
import { Contact, useContactStore } from '../../../stores/contactStore';
import { usePermission } from '../../../hooks/usePermission';
import { api, integrationsApi } from '../../../lib/api';
import { explainEnrichmentError } from '@/lib/enrichmentError';
import { useScrollLock } from '../../../lib/useScrollLock';
import { toast } from 'sonner';
import { EnrichmentPanel } from './EnrichmentPanel';
import { EnrichmentCatalogModal } from './EnrichmentCatalogModal';
import { AddContactModal } from './AddContactModal';
import { CrmExportModal } from './CrmExportModal';
import BookMeetingModal from './BookMeetingModal';
import ComposeEmailModal from './ComposeEmailModal';
import { CalendarPlus } from '@phosphor-icons/react';
import { SourceCitation } from './SectionCard';
import { cn, getInitials, formatDate, isUndeliverableContact } from '@/lib/utils';
import { handleTabListKeyDown } from '@/lib/rovingTabs';
import { cardTitleStyle as sectionTitleStyle } from '@/lib/styleKit';
import { seniorityLabel } from '@/lib/seniority';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ContactDetailModalProps {
  contact: Contact;
  isOpen: boolean;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Details Panel (merged Overview + Verification)
// ---------------------------------------------------------------------------

interface DetailsPanelProps {
  contact: Contact;
  allEmails: any[];
  apolloEnriching: boolean;
  onApolloEnrich: () => void;
  hunterVerification: any;
  hunterVerifying: boolean;
  hunterEnriching: boolean;
  onHunterVerify: () => void;
  onHunterEnrich: () => void;
}

// Fit/Intent score breakdown factor metadata (labels + per-factor caps,
// mirrors backend lead_scoring/config.py defaults).
const FIT_FACTOR_META: { key: string; label: string; max: number; gate: 'A' | 'B' }[] = [
  { key: 'seniority', label: 'Seniority / role', max: 8, gate: 'A' },
  { key: 'industry', label: 'Industry fit', max: 6, gate: 'A' },
  { key: 'company_size', label: 'Company size', max: 4, gate: 'A' },
  { key: 'geo', label: 'Geo serviceability', max: 2, gate: 'A' },
  { key: 'decision_authority', label: 'Decision authority', max: 6, gate: 'B' },
  { key: 'champion', label: 'Champion score', max: 4, gate: 'B' },
  { key: 'pain_gap', label: 'Pain / tech-gap match', max: 5, gate: 'B' },
  { key: 'budget_timing', label: 'Budget timing', max: 2, gate: 'B' },
  { key: 'verified_profile', label: 'Verified profile', max: 3, gate: 'B' },
];

const PEAK_TIER_LABELS: Record<string, string> = {
  meeting_booked: 'Meeting booked',
  meeting_inferred: 'Inferred meeting',
  meeting_requested: 'Meeting requested',
  positive_reply: 'Positive reply',
  corroboration: 'Multiple high-intent actions',
  human_reply: 'Human reply',
  high_intent_action: 'High-intent action',
  mid_value_content: 'Mid-value content',
  clicked_link: 'Clicked link',
};

function ScoreBar({ value, max, tone }: { value: number; max: number; tone: 'fit' | 'intent' }) {
  const pct = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
  const fill = tone === 'fit'
    ? 'bg-[#5e6ad2]/80 dark:bg-[#7170ff]/80'
    : 'bg-[#7170ff]/80 dark:bg-[#7170ff]/80';
  return (
    <div className="h-1 rounded-full bg-border/60 dark:bg-white/[0.08] overflow-hidden">
      <div className={cn('h-full rounded-full', fill)} style={{ width: `${pct}%` }} />
    </div>
  );
}

function LeadScorePanel({ contact }: { contact: Contact }) {
  const [open, setOpen] = useState(false);

  const lead = Math.round(contact.lead_score ?? 0);
  const fit = contact.fit_score ?? 0;
  const intent = contact.intent_score ?? 0;
  const bd: any = contact.score_breakdown || {};
  const factors: Record<string, number> = bd?.fit?.factors || {};
  const intentBd: any = bd?.intent || {};
  const dq: any = bd?.disqualifiers || {};
  const hasBreakdown = !!bd && (bd.fit || bd.intent);

  const reasons: string[] = Array.isArray(dq?.reasons) ? dq.reasons : [];
  const frozen = !!dq?.hard;
  const peakTier = intentBd?.intent_peak_tier;

  // Headline colour scales with the 0–100 score.
  const scoreTone = frozen
    ? 'text-muted-foreground dark:text-[#8a8f98]'
    : lead >= 60 ? 'text-emerald-600 dark:text-emerald-400'
    : 'text-[#5e6ad2] dark:text-[#a5b4fc]';

  return (
    <div className="rounded-lg border border-border/60 dark:border-white/[0.08] bg-card dark:bg-white/[0.02] overflow-hidden">
      {/* Clickable header */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted/40 dark:hover:bg-white/[0.03] transition-colors text-left"
      >
        <div className="flex-1 min-w-0">
          <div className="inline-flex items-center gap-1.5 text-muted-foreground dark:text-[#8a8f98] uppercase" style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.08em' }}>
            <Gauge className="h-3 w-3" weight="regular" />
            Lead Score
          </div>
          <div className="flex items-baseline gap-1.5 mt-0.5">
            <span className={cn('tabular-nums', scoreTone)} style={{ fontSize: '22px', fontWeight: 590, letterSpacing: '-0.5px' }}>{lead}</span>
            <span className="text-muted-foreground dark:text-[#8a8f98] tabular-nums" style={{ fontSize: '12px', fontWeight: 510 }}>/ 100</span>
            {frozen && (
              <span className="ml-1 px-1.5 py-0.5 rounded text-rose-600 bg-rose-500/10 border border-rose-500/20 dark:text-rose-400" style={{ fontSize: '9px', fontWeight: 590, letterSpacing: '0.03em' }}>FROZEN</span>
            )}
          </div>
          <div className="flex items-center gap-2.5 mt-1 text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '10.5px', fontWeight: 510, letterSpacing: '-0.05px' }}>
            <span className="inline-flex items-center gap-1"><TargetPhosphor className="h-2.5 w-2.5 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="fill" />Fit <span className="tabular-nums text-foreground dark:text-[#f7f8f8]">{Math.round(fit)}</span>/40</span>
            <span className="inline-flex items-center gap-1"><Lightning className="h-2.5 w-2.5 text-[#7170ff] dark:text-[#a5b4fc]" weight="fill" />Intent <span className="tabular-nums text-foreground dark:text-[#f7f8f8]">{Math.round(intent)}</span>/60</span>
          </div>
        </div>
        <CaretDown
          className={cn('h-4 w-4 text-muted-foreground dark:text-[#8a8f98] transition-transform duration-200 flex-shrink-0', open && 'rotate-180')}
          weight="bold"
        />
      </button>

      {/* Accordion: per-field breakdown */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="px-3 pb-3 pt-1 border-t border-border/50 dark:border-white/[0.06] space-y-3">
              {!hasBreakdown ? (
                <p className="text-muted-foreground dark:text-[#8a8f98] py-1" style={{ fontSize: '11px', letterSpacing: '-0.1px' }}>
                  Not scored yet — the score updates automatically on enrichment and engagement.
                </p>
              ) : (
                <>
                  {/* FIT factors */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="inline-flex items-center gap-1 text-[#5e6ad2] dark:text-[#a5b4fc] uppercase" style={{ fontSize: '9.5px', fontWeight: 590, letterSpacing: '0.06em' }}>
                        <TargetPhosphor className="h-2.5 w-2.5" weight="fill" /> Fit — who they are
                      </span>
                      <span className="tabular-nums text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '10px', fontWeight: 590 }}>{Math.round(fit)} / 40</span>
                    </div>
                    <div className="space-y-1.5">
                      {FIT_FACTOR_META.map((f) => {
                        const v = Number(factors[f.key] ?? 0);
                        return (
                          <div key={f.key}>
                            <div className="flex items-center justify-between" style={{ fontSize: '11px', letterSpacing: '-0.1px' }}>
                              <span className="text-foreground dark:text-[#f7f8f8] truncate flex items-center gap-1">
                                {f.label}
                                <span className="text-muted-foreground/60 dark:text-[#8a8f98]/60" style={{ fontSize: '8.5px', fontWeight: 590 }}>{f.gate === 'A' ? 'ACQ' : 'ENR'}</span>
                              </span>
                              <span className="tabular-nums text-muted-foreground dark:text-[#8a8f98] flex-shrink-0 ml-2">
                                <span className={cn(v > 0 && 'text-foreground dark:text-[#f7f8f8]')} style={{ fontWeight: 590 }}>{v}</span>/{f.max}
                              </span>
                            </div>
                            <div className="mt-0.5"><ScoreBar value={v} max={f.max} tone="fit" /></div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* INTENT breakdown */}
                  <div className="pt-2 border-t border-border/50 dark:border-white/[0.06]">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="inline-flex items-center gap-1 text-[#7170ff] dark:text-[#a5b4fc] uppercase" style={{ fontSize: '9.5px', fontWeight: 590, letterSpacing: '0.06em' }}>
                        <Lightning className="h-2.5 w-2.5" weight="fill" /> Intent — what they do
                      </span>
                      <span className="tabular-nums text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '10px', fontWeight: 590 }}>{Math.round(intent)} / 60</span>
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center justify-between" style={{ fontSize: '11px', letterSpacing: '-0.1px' }}>
                        <span className="text-muted-foreground dark:text-[#8a8f98]">Peak signal</span>
                        <span className="text-foreground dark:text-[#f7f8f8]" style={{ fontWeight: 590 }}>
                          {peakTier ? (PEAK_TIER_LABELS[peakTier] || peakTier) : '—'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between" style={{ fontSize: '11px', letterSpacing: '-0.1px' }}>
                        <span className="text-muted-foreground dark:text-[#8a8f98]">Peak value (after decay)</span>
                        <span className="tabular-nums text-foreground dark:text-[#f7f8f8]" style={{ fontWeight: 590 }}>{Number(intentBd?.peak_base_decayed ?? 0)}</span>
                      </div>
                      <div className="flex items-center justify-between" style={{ fontSize: '11px', letterSpacing: '-0.1px' }}>
                        <span className="text-muted-foreground dark:text-[#8a8f98]">Breadth bonus</span>
                        <span className="tabular-nums text-foreground dark:text-[#f7f8f8]" style={{ fontWeight: 590 }}>+{Number(intentBd?.breadth ?? 0)}</span>
                      </div>
                      {!!intentBd?.soft_negative_damped && (
                        <div className="flex items-center justify-between text-amber-600 dark:text-amber-400" style={{ fontSize: '10.5px', letterSpacing: '-0.1px' }}>
                          <span>Damped — “not interested” cooldown</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Exclusions / disqualifiers */}
                  {(reasons.length > 0 || frozen) && (
                    <div className="pt-2 border-t border-border/50 dark:border-white/[0.06]">
                      <div className="flex flex-wrap gap-1">
                        {frozen && (
                          <span className="px-1.5 py-0.5 rounded text-rose-600 bg-rose-500/10 border border-rose-500/20 dark:text-rose-400" style={{ fontSize: '10px', fontWeight: 510 }}>
                            Score frozen ({(contact.email_status || 'disqualified')})
                          </span>
                        )}
                        {reasons.map((r) => (
                          <span key={r} className="px-1.5 py-0.5 rounded text-amber-700 bg-amber-500/10 border border-amber-500/20 dark:text-amber-400" style={{ fontSize: '10px', fontWeight: 510 }}>
                            {r.replace(/_/g, ' ')}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <p className="text-muted-foreground/70 dark:text-[#8a8f98]/70" style={{ fontSize: '9.5px', letterSpacing: '-0.05px' }}>
                    Lead Score = Fit + Intent (0–100). Updates on enrichment &amp; engagement.
                  </p>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function DetailsPanel({
  contact,
  allEmails,
  apolloEnriching,
  onApolloEnrich,
  hunterVerification,
  hunterVerifying,
  hunterEnriching,
  onHunterVerify,
  onHunterEnrich,
}: DetailsPanelProps) {
  // Contact flagged invalid/fake_email — provider enrichment (Apollo/Hunter)
  // would burn credits on a dead address, so the backend refuses it. Disable
  // the Enrich actions here to match (bug 21670). Hunter "Verify" stays enabled
  // since it's a deliverability check, not enrichment.
  const undeliverable = isUndeliverableContact(contact);
  const undeliverableHint =
    "This contact's email is flagged invalid. Enrichment is disabled to avoid wasting credits.";
  const sectionLabelCls =
    'inline-flex items-center gap-1.5 text-muted-foreground dark:text-[#8a8f98] uppercase';
  const sectionLabelStyle = { fontSize: '10px', fontWeight: 590, letterSpacing: '0.08em' } as const;
  const dividerCls = 'border-t border-border/50 dark:border-white/[0.06]';

  return (
    <div className="space-y-4">
      {/* Lead Score (Fit + Intent) — click to expand the per-field breakdown */}
      <LeadScorePanel contact={contact} />

      {/* Title */}
      {contact.job_title && (
        <div>
          <div className={sectionLabelCls} style={sectionLabelStyle}>
            <BriefcasePhosphor className="h-3 w-3" weight="regular" />
            Title
          </div>
          <p
            className="text-foreground dark:text-[#f7f8f8] mt-1"
            style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
          >
            {contact.job_title}
          </p>
          {(contact.department || contact.seniority_level) && (
            <p
              className="text-muted-foreground dark:text-[#8a8f98] mt-0.5"
              style={{ fontSize: '11px', letterSpacing: '-0.1px' }}
            >
              {[contact.department, seniorityLabel(contact.seniority_level)].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>
      )}

      {/* Company */}
      {contact.company_name && (
        <>
          <div className={dividerCls} />
          <div>
            <div className={sectionLabelCls} style={sectionLabelStyle}>
              <Buildings className="h-3 w-3" weight="regular" />
              Company
            </div>
            <p
              className="text-foreground dark:text-[#f7f8f8] mt-1"
              style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
            >
              {contact.company_name}
            </p>
            <div className="mt-0.5 space-y-0.5">
              {contact.company_industry && (
                <p
                  className="text-muted-foreground dark:text-[#8a8f98]"
                  style={{ fontSize: '11px', letterSpacing: '-0.1px' }}
                >
                  {contact.company_industry}
                </p>
              )}
              {contact.company_size && (
                <p
                  className="text-muted-foreground dark:text-[#8a8f98] tabular-nums"
                  style={{ fontSize: '11px', letterSpacing: '-0.1px' }}
                >
                  {contact.company_size} employees
                </p>
              )}
            </div>
            {contact.company_website && (
              <a
                href={contact.company_website.startsWith('http') ? contact.company_website : `https://${contact.company_website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 mt-1.5 text-[#5e6ad2] hover:text-[#7170ff] dark:text-[#a5b4fc] dark:hover:text-[#c5cbff] underline-offset-2 hover:underline transition-colors"
                style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
              >
                {contact.company_website.replace(/^https?:\/\//, '')}
                <ArrowSquareOut className="h-3 w-3" weight="bold" />
              </a>
            )}
          </div>
        </>
      )}

      {/* Contact — phone + location only */}
      {(contact.phone || contact.city || contact.state || contact.country) && (
        <>
          <div className={dividerCls} />
          <div className="space-y-2">
            {contact.phone && (
              <DetailRow icon={PhonePhosphor} label="Phone" value={contact.phone} />
            )}
            {(contact.city || contact.state || contact.country) && (
              <DetailRow
                icon={MapPinPhosphor}
                label="Location"
                value={[contact.city, contact.state, contact.country].filter(Boolean).join(', ')}
              />
            )}
          </div>
        </>
      )}

      {/* Profiles — social links get their own section, not tucked under Location */}
      {(contact.linkedin_url || contact.twitter_url) && (
        <>
          <div className={dividerCls} />
          <div>
            <div className={sectionLabelCls} style={sectionLabelStyle}>
              <LinkSimple className="h-3 w-3" weight="regular" />
              Profiles
            </div>
            <div className="flex items-center gap-1.5 mt-2">
              {contact.linkedin_url && (
                <a
                  href={contact.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border bg-[#0A66C2]/10 text-[#0A66C2] border-[#0A66C2]/20 hover:bg-[#0A66C2]/15 dark:bg-[#0A66C2]/[0.12] dark:text-[#5e9ad8] dark:border-[#0A66C2]/25 dark:hover:bg-[#0A66C2]/[0.18] transition-colors"
                  style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
                >
                  <LinkedinLogo className="h-3 w-3" weight="fill" /> LinkedIn
                </a>
              )}
              {contact.twitter_url && (
                <a
                  href={contact.twitter_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border bg-muted/50 text-foreground border-border/60 hover:bg-muted/70 dark:bg-white/[0.04] dark:text-[#f7f8f8] dark:border-white/[0.08] dark:hover:bg-white/[0.06] transition-colors"
                  style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
                >
                  <TwitterLogo className="h-3 w-3" weight="bold" /> Twitter
                </a>
              )}
            </div>
          </div>
        </>
      )}

      {/* Tags */}
      {contact.tags && contact.tags.length > 0 && (
        <>
          <div className={dividerCls} />
          <div className="flex flex-wrap gap-1">
            {contact.tags.map((tag, idx) => (
              <span
                key={idx}
                className="inline-flex items-center px-2 py-0.5 rounded-full border bg-[#5e6ad2]/10 text-[#5e6ad2] border-[#5e6ad2]/25 dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc] dark:border-[#5e6ad2]/30"
                style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
              >
                {tag}
              </span>
            ))}
          </div>
        </>
      )}

      {/* Metadata */}
      <div className={dividerCls} />
      <div className="space-y-1.5">
        <MetaRow label="Source" value={(contact.import_source || 'Manual').replace(/^\w/, (c) => c.toUpperCase())} />
        <MetaRow
          label="Created"
          value={contact.created_at ? formatDate(contact.created_at, 'PP') : '—'}
        />
        {contact.updated_at && (
          <MetaRow label="Updated" value={formatDate(contact.updated_at, 'PP')} />
        )}
        {contact.last_email_sent && (
          <MetaRow label="Last email" value={formatDate(contact.last_email_sent, 'PP')} />
        )}
      </div>

      {/* Verification */}
      <div className={dividerCls} />
      <div className="space-y-3">
        <div className={sectionLabelCls} style={sectionLabelStyle}>
          <ShieldCheck className="h-3 w-3" weight="regular" />
          Verification
        </div>

        {/* Apollo */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span
              className="text-foreground dark:text-[#f7f8f8]"
              style={{ fontSize: '12px', fontWeight: 590, letterSpacing: '-0.1px' }}
            >
              Apollo
            </span>
            <Button
              variant="outline"
              size="sm"
              icon={apolloEnriching ? <CircleNotch className="h-3 w-3 animate-spin" /> : <MagnifyingGlass className="h-3 w-3" weight="bold" />}
              onClick={onApolloEnrich}
              disabled={apolloEnriching || undeliverable}
              title={undeliverable ? undeliverableHint : undefined}
              className="h-6 px-2"
            >
              <span style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}>
                {apolloEnriching ? '…' : 'Enrich'}
              </span>
            </Button>
          </div>
          {allEmails.length > 0 ? (
            <div className="space-y-1">
              {allEmails.map((em: any, idx: number) => (
                <div
                  key={idx}
                  className="flex items-center justify-between gap-2 py-1.5 px-2 rounded-md border bg-card border-border/50 dark:bg-white/[0.02] dark:border-white/[0.06]"
                >
                  <span
                    className="text-foreground dark:text-[#f7f8f8] truncate"
                    style={{ fontFamily: 'ui-monospace, SF Mono, Menlo, monospace', fontSize: '11px', letterSpacing: '-0.05px' }}
                  >
                    {em.email}
                  </span>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {em.type && (
                      <span
                        className={cn(
                          'px-1.5 py-0.5 rounded border capitalize',
                          em.type === 'primary'
                            ? 'bg-[#5e6ad2]/10 text-[#5e6ad2] border-[#5e6ad2]/25 dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc] dark:border-[#5e6ad2]/30'
                            : NEUTRAL_PILL
                        )}
                        style={{ fontSize: '10px', fontWeight: 510, letterSpacing: '-0.05px' }}
                      >
                        {em.type}
                      </span>
                    )}
                    {em.status && (
                      <span
                        className={cn(
                          'px-1.5 py-0.5 rounded border capitalize',
                          em.status === 'verified'
                            ? 'bg-emerald-500/10 text-emerald-700 border-emerald-500/25 dark:bg-emerald-500/[0.12] dark:text-emerald-400 dark:border-emerald-500/25'
                            : 'bg-amber-500/10 text-amber-700 border-amber-500/25 dark:bg-amber-500/[0.12] dark:text-amber-400 dark:border-amber-500/25'
                        )}
                        style={{ fontSize: '10px', fontWeight: 510, letterSpacing: '-0.05px' }}
                      >
                        {em.status}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p
              className="text-muted-foreground dark:text-[#8a8f98]"
              style={{ fontSize: '11px', letterSpacing: '-0.05px', lineHeight: 1.5 }}
            >
              Click Enrich to verify and find secondary emails.
            </p>
          )}
        </div>

        {/* Hunter */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span
              className="text-foreground dark:text-[#f7f8f8]"
              style={{ fontSize: '12px', fontWeight: 590, letterSpacing: '-0.1px' }}
            >
              Hunter.io
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                icon={hunterVerifying ? <CircleNotch className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3" weight="bold" />}
                onClick={onHunterVerify}
                disabled={hunterVerifying}
                className="h-6 px-2"
              >
                <span style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}>
                  {hunterVerifying ? '…' : 'Verify'}
                </span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                icon={hunterEnriching ? <CircleNotch className="h-3 w-3 animate-spin" /> : <MagnifyingGlass className="h-3 w-3" weight="bold" />}
                onClick={onHunterEnrich}
                disabled={hunterEnriching || undeliverable}
                title={undeliverable ? undeliverableHint : undefined}
                className="h-6 px-2"
              >
                <span style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}>
                  {hunterEnriching ? '…' : 'Enrich'}
                </span>
              </Button>
            </div>
          </div>
          {hunterVerification && hunterVerification.success !== false ? (
            <div className="grid grid-cols-2 gap-1.5">
              <VerifyTile label="Status">
                <span className={cn(
                  'inline-flex items-center gap-1 capitalize',
                  hunterVerification.status === 'valid' ? 'text-emerald-600 dark:text-emerald-400' :
                  hunterVerification.status === 'invalid' ? 'text-red-600 dark:text-red-400' :
                  'text-amber-600 dark:text-amber-400'
                )} style={{ fontSize: '12px', fontWeight: 590, letterSpacing: '-0.1px' }}>
                  {hunterVerification.status === 'valid' && <CheckCircle className="h-3 w-3" weight="fill" />}
                  {hunterVerification.status === 'invalid' && <XCirclePhosphor className="h-3 w-3" weight="fill" />}
                  {hunterVerification.status === 'accept_all' && <Warning className="h-3 w-3" weight="fill" />}
                  {hunterVerification.status}
                </span>
              </VerifyTile>
              <VerifyTile label="Score">
                <span className="text-foreground dark:text-[#f7f8f8] tabular-nums"
                  style={{ fontSize: '13px', fontWeight: 590, letterSpacing: '-0.13px' }}>
                  {hunterVerification.score ?? '—'}
                </span>
              </VerifyTile>
              <VerifyTile label="MX">
                {hunterVerification.mx_records
                  ? <CheckCircle className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" weight="fill" />
                  : <XCirclePhosphor className="h-3.5 w-3.5 text-red-600 dark:text-red-400" weight="fill" />}
              </VerifyTile>
              <VerifyTile label="SMTP">
                {hunterVerification.smtp_check
                  ? <CheckCircle className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" weight="fill" />
                  : <XCirclePhosphor className="h-3.5 w-3.5 text-red-600 dark:text-red-400" weight="fill" />}
              </VerifyTile>
            </div>
          ) : (
            <p
              className="text-muted-foreground dark:text-[#8a8f98]"
              style={{ fontSize: '11px', letterSpacing: '-0.05px', lineHeight: 1.5 }}
            >
              Verify deliverability or enrich via Hunter.io.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function DetailRow({ icon: Icon, label, value }: { icon: any; label: string; value: string }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="h-3.5 w-3.5 text-muted-foreground dark:text-[#8a8f98] mt-0.5 flex-shrink-0" weight="regular" />
      <div className="flex-1 min-w-0">
        <p
          className="text-muted-foreground dark:text-[#8a8f98] uppercase"
          style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.06em' }}
        >
          {label}
        </p>
        <p
          className="text-foreground dark:text-[#f7f8f8] mt-0.5"
          style={{ fontSize: '12px', letterSpacing: '-0.1px', lineHeight: 1.4 }}
        >
          {value}
        </p>
      </div>
    </div>
  );
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span
        className="text-muted-foreground dark:text-[#8a8f98]"
        style={{ fontSize: '11px', letterSpacing: '-0.05px' }}
      >
        {label}
      </span>
      <span
        className="text-foreground dark:text-[#f7f8f8] truncate tabular-nums"
        style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.05px' }}
      >
        {value}
      </span>
    </div>
  );
}

function VerifyTile({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="p-2 rounded-md border bg-card border-border/50 dark:bg-white/[0.02] dark:border-white/[0.06]">
      <p
        className="text-muted-foreground dark:text-[#8a8f98] uppercase mb-1"
        style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.06em' }}
      >
        {label}
      </p>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Financial Results Panel
// ---------------------------------------------------------------------------

function formatCurrency(val: number | null | undefined): string {
  if (val == null) return '—'
  const abs = Math.abs(val)
  if (abs >= 1e12) return `$${(val / 1e12).toFixed(1)}T`
  if (abs >= 1e9) return `$${(val / 1e9).toFixed(1)}B`
  if (abs >= 1e6) return `$${(val / 1e6).toFixed(1)}M`
  if (abs >= 1e3) return `$${(val / 1e3).toFixed(1)}K`
  return `$${val.toLocaleString()}`
}

function FinancialTable({ title, icon, rows, columns }: {
  title: string; icon: React.ReactNode;
  rows: any[]; columns: { key: string; label: string; format?: (v: any) => string }[]
}) {
  if (!rows || rows.length === 0) return null
  return (
    <div className="border border-border/50 dark:border-white/[0.08] dark:bg-white/[0.02] rounded-lg overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-border/50 dark:border-white/[0.06] bg-muted/40 dark:bg-white/[0.03]">
        {icon}
        <span className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px', fontWeight: 590, letterSpacing: '-0.13px' }}>{title}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead>
            <tr className="border-b border-border/40 dark:border-white/[0.06]">
              <th className="text-left px-3 py-2 text-muted-foreground dark:text-[#8a8f98] uppercase tracking-[0.06em]" style={{ fontSize: '10px', fontWeight: 590 }}>Period</th>
              {columns.map((c) => (
                <th key={c.key} className="text-right px-3 py-2 text-muted-foreground dark:text-[#8a8f98] uppercase tracking-[0.06em]" style={{ fontSize: '10px', fontWeight: 590 }}>{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className={i % 2 === 0 ? '' : 'bg-muted/30 dark:bg-white/[0.015]'}>
                <td className="px-3 py-2 text-foreground dark:text-[#f7f8f8]" style={{ fontWeight: 510 }}>{row.period || row.date?.slice(0, 4) || '—'}</td>
                {columns.map((c) => (
                  <td key={c.key} className="text-right px-3 py-2 text-foreground dark:text-[#d0d6e0] tabular-nums">
                    {c.format ? c.format(row[c.key]) : (row[c.key] != null ? String(row[c.key]) : '—')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function FinancialResultsPanel({ results, onEnrich }: { results: Record<string, any>; onEnrich: () => void }) {
  const fmp = results.fmp?.status === 'completed' ? results.fmp.data : null
  const av = results.alpha_vantage?.status === 'completed' ? results.alpha_vantage.data : null
  const edgar = results.sec_edgar?.status === 'completed' ? results.sec_edgar.data : null

  const hasAny = fmp || av || edgar

  if (!hasAny) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center py-16">
        <div className="h-12 w-12 rounded-md bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center mb-4">
          <CurrencyDollar className="w-5 h-5 text-muted-foreground dark:text-[#8a8f98]" />
        </div>
        <h3 className="text-foreground dark:text-[#f7f8f8] mb-1" style={{ fontSize: '15px', fontWeight: 590, letterSpacing: '-0.182px' }}>No financial data yet</h3>
        <p className="text-muted-foreground dark:text-[#8a8f98] mb-5 max-w-xs" style={{ fontSize: '13px', letterSpacing: '-0.13px' }}>
          Enrich this contact to get income statements, balance sheets, cash flow, and valuation metrics.
        </p>
        <Button variant="default" size="sm" icon={<TrendUp className="w-4 h-4" weight="bold" />} onClick={onEnrich}>
          Get Financial Data
        </Button>
      </div>
    )
  }

  // Use whichever provider has data (prefer FMP > AV > EDGAR)
  const income = fmp?.income_statement || av?.income_statement || []
  const balance = fmp?.balance_sheet || av?.balance_sheet || []
  const cashFlow = fmp?.cash_flow || av?.cash_flow || []
  const metrics = fmp?.metrics || av?.metrics || {}
  const filings = edgar?.recent_filings || []
  const edgarFacts = edgar?.financial_facts || null

  // Show provider source
  const sourceLabel = fmp ? (results.fmp?.provider_meta?.ticker || 'FMP')
    : av ? (results.alpha_vantage?.provider_meta?.ticker || 'Alpha Vantage')
    : 'SEC EDGAR'

  return (
    <div className="space-y-4">
      {/* Meta */}
      <div className="flex items-center gap-3 text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
        <span className="inline-flex items-center gap-1"><ChartBar className="w-3 h-3" /> Source: {sourceLabel}</span>
      </div>

      {/* Valuation Metrics */}
      {Object.keys(metrics).length > 0 && (
        <div className="border border-border/50 dark:border-white/[0.08] dark:bg-white/[0.02] rounded-lg overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2.5 border-b border-border/50 dark:border-white/[0.06] bg-muted/40 dark:bg-white/[0.03]">
            <TrendUp className="w-4 h-4 text-[#5e6ad2] dark:text-[#a5b4fc]" />
            <span className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px', fontWeight: 590, letterSpacing: '-0.13px' }}>Valuation & Market Data</span>
          </div>
          <div className="grid grid-cols-3 gap-px bg-border/40 dark:bg-white/[0.06]">
            {[
              { label: 'Market Cap', value: formatCurrency(metrics.market_cap) },
              { label: 'P/E Ratio', value: metrics.pe_ratio != null ? metrics.pe_ratio.toFixed(1) : '—' },
              { label: 'EPS', value: metrics.eps != null ? `$${metrics.eps.toFixed(2)}` : '—' },
              { label: 'Div. Yield', value: metrics.dividend_yield != null ? `${(metrics.dividend_yield * 100).toFixed(2)}%` : '—' },
              { label: '52W High', value: metrics['52_week_high'] != null ? `$${metrics['52_week_high']}` : '—' },
              { label: '52W Low', value: metrics['52_week_low'] != null ? `$${metrics['52_week_low']}` : '—' },
              { label: 'Sector', value: metrics.sector || '—' },
              { label: 'Industry', value: metrics.industry || '—' },
              { label: 'Exchange', value: metrics.exchange || '—' },
            ].map((m) => (
              <div key={m.label} className="bg-card dark:bg-[#0f1011] px-3 py-2.5">
                <p className="text-muted-foreground dark:text-[#62666d] uppercase tracking-[0.06em]" style={{ fontSize: '10px', fontWeight: 590 }}>{m.label}</p>
                <p className="text-foreground dark:text-[#f7f8f8] mt-0.5 tabular-nums" style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}>{m.value}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Income Statement */}
      <FinancialTable
        title="Income Statement"
        icon={<FileText className="w-4 h-4 text-[#5e6ad2] dark:text-[#a5b4fc]" />}
        rows={income}
        columns={[
          { key: 'revenue', label: 'Revenue', format: formatCurrency },
          { key: 'gross_profit', label: 'Gross Profit', format: formatCurrency },
          { key: 'operating_income', label: 'Operating Inc.', format: formatCurrency },
          { key: 'net_income', label: 'Net Income', format: formatCurrency },
        ]}
      />

      {/* Balance Sheet */}
      <FinancialTable
        title="Balance Sheet"
        icon={<ChartBar className="w-4 h-4 text-[#5e6ad2] dark:text-[#a5b4fc]" />}
        rows={balance}
        columns={[
          { key: 'total_assets', label: 'Total Assets', format: formatCurrency },
          { key: 'total_liabilities', label: 'Total Liab.', format: formatCurrency },
          { key: 'total_equity', label: 'Equity', format: formatCurrency },
          { key: 'cash', label: 'Cash', format: formatCurrency },
          { key: 'total_debt', label: 'Debt', format: formatCurrency },
        ]}
      />

      {/* Cash Flow */}
      <FinancialTable
        title="Cash Flow Statement"
        icon={<CurrencyDollar className="w-4 h-4 text-[#5e6ad2] dark:text-[#a5b4fc]" />}
        rows={cashFlow}
        columns={[
          { key: 'operating_cash_flow', label: 'Operating', format: formatCurrency },
          { key: 'investing_cash_flow', label: 'Investing', format: formatCurrency },
          { key: 'financing_cash_flow', label: 'Financing', format: formatCurrency },
          { key: 'free_cash_flow', label: 'Free Cash Flow', format: formatCurrency },
        ]}
      />

      {/* EDGAR Financial Facts (if no FMP/AV data) */}
      {edgarFacts && !fmp && !av && (
        <div className="border border-border/50 dark:border-white/[0.08] dark:bg-white/[0.02] rounded-lg overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2.5 border-b border-border/50 dark:border-white/[0.06] bg-muted/40 dark:bg-white/[0.03]">
            <FileText className="w-4 h-4 text-muted-foreground dark:text-[#8a8f98]" />
            <span className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px', fontWeight: 590, letterSpacing: '-0.13px' }}>SEC EDGAR Financial Facts</span>
          </div>
          <div className="p-3 space-y-2">
            {Object.entries(edgarFacts).map(([key, values]: [string, any]) => {
              if (!Array.isArray(values) || values.length === 0) return null
              return (
                <div key={key} className="flex items-center justify-between text-[12px]">
                  <span className="text-muted-foreground dark:text-[#8a8f98] capitalize">{key.replace(/_/g, ' ')}</span>
                  <div className="flex gap-3">
                    {values.slice(0, 3).map((v: any, i: number) => (
                      <span key={i} className="tabular-nums text-foreground dark:text-[#f7f8f8]">
                        {v.period}: {formatCurrency(v.value)}
                      </span>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* SEC Filings */}
      {filings.length > 0 && (
        <div className="border border-border/50 dark:border-white/[0.08] dark:bg-white/[0.02] rounded-lg overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2.5 border-b border-border/50 dark:border-white/[0.06] bg-muted/40 dark:bg-white/[0.03]">
            <FileText className="w-4 h-4 text-[#5e6ad2] dark:text-[#a5b4fc]" />
            <span className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px', fontWeight: 590, letterSpacing: '-0.13px' }}>Recent SEC Filings</span>
          </div>
          <div className="divide-y divide-border/40 dark:divide-white/[0.06]">
            {filings.slice(0, 10).map((f: any, i: number) => (
              <div key={i} className="flex items-center gap-3 px-3 py-2 text-[12px]">
                <span className="font-mono text-foreground dark:text-[#f7f8f8] w-12" style={{ fontWeight: 510 }}>{f.form_type}</span>
                <span className="text-muted-foreground dark:text-[#8a8f98] tabular-nums">{f.filing_date}</span>
                <span className="flex-1 text-muted-foreground dark:text-[#8a8f98] truncate">{f.description}</span>
                {f.url && (
                  <a href={f.url} target="_blank" rel="noopener noreferrer" className="text-[#5e6ad2] dark:text-[#a5b4fc] hover:underline inline-flex items-center gap-0.5 flex-shrink-0" style={{ fontWeight: 510 }}>
                    View <ArrowSquareOut className="w-3 h-3" />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function NewsResultsPanel({ results, onEnrich }: { results: any; onEnrich: () => void }) {
  if (!results || results.status !== 'completed') {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center py-16">
        <div className="h-12 w-12 rounded-md bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center mb-4">
          <Newspaper className="w-5 h-5 text-muted-foreground dark:text-[#8a8f98]" />
        </div>
        <p className="text-foreground dark:text-[#f7f8f8] mb-1" style={{ fontSize: '15px', fontWeight: 590, letterSpacing: '-0.182px' }}>No news data yet</p>
        <p className="text-muted-foreground dark:text-[#8a8f98] mb-4 max-w-[240px]" style={{ fontSize: '13px', letterSpacing: '-0.13px' }}>
          Run news enrichment to find company, person, and industry news.
        </p>
        <Button variant="default" size="sm" icon={<Newspaper className="w-4 h-4" weight="bold" />} onClick={onEnrich}>
          Get News
        </Button>
      </div>
    )
  }

  const data = results.data || {}
  const companyNews = data.company_news || []
  const personNews = data.person_news || []
  const industryNews = data.industry_news || []
  const summary = data.summary || {}
  const providerMeta = results.provider_meta || {}

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '15px', fontWeight: 590, letterSpacing: '-0.182px' }}>
          <Newspaper className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc]" />
          News Enrichment
        </h3>
        <Button variant="ghost" size="sm" onClick={onEnrich}>
          Re-fetch
        </Button>
      </div>

      {/* Summary bar */}
      <div className="flex flex-wrap items-center gap-3 text-muted-foreground dark:text-[#8a8f98] bg-muted/40 dark:bg-white/[0.04] border border-border/50 dark:border-white/[0.06] rounded-lg px-3 py-2" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
        <span className="text-foreground dark:text-[#f7f8f8] tabular-nums" style={{ fontWeight: 510 }}>{summary.total_articles || 0} articles</span>
        {summary.company_count > 0 && <span className="tabular-nums">{summary.company_count} company</span>}
        {summary.person_count > 0 && <span className="tabular-nums">{summary.person_count} person</span>}
        {summary.industry_count > 0 && <span className="tabular-nums">{summary.industry_count} industry</span>}
        {providerMeta.news_providers_used?.length > 0 && (
          <span className="ml-auto uppercase tracking-[0.06em] text-[11px]">via {providerMeta.news_providers_used.join(', ')}</span>
        )}
      </div>

      {/* Company News */}
      {companyNews.length > 0 && (
        <NewsSection title="Company News" articles={companyNews} icon={<Buildings className="w-3.5 h-3.5 text-muted-foreground dark:text-[#8a8f98]" />} />
      )}

      {/* Person News */}
      {personNews.length > 0 && (
        <NewsSection title="Person News" articles={personNews} icon={<User className="w-3.5 h-3.5 text-muted-foreground dark:text-[#8a8f98]" />} />
      )}

      {/* Industry News */}
      {industryNews.length > 0 && (
        <NewsSection title="Industry News" articles={industryNews} icon={<TrendUp className="w-3.5 h-3.5 text-muted-foreground dark:text-[#8a8f98]" />} />
      )}
    </div>
  )
}

function NewsSection({ title, articles, icon }: {
  title: string
  articles: any[]
  icon: React.ReactNode
}) {
  return (
    <div>
      <h4 className="mb-2 flex items-center gap-1.5 text-muted-foreground dark:text-[#8a8f98] uppercase tracking-[0.06em]" style={{ fontSize: '11px', fontWeight: 590 }}>
        {icon}
        {title}
        <span className="opacity-60 tabular-nums" style={{ fontWeight: 510 }}>({articles.length})</span>
      </h4>
      <div className="space-y-2">
        {articles.filter((a: any) => a && typeof a === 'object').map((article: any, idx: number) => (
          <div key={idx} className="rounded-md border border-border/50 dark:border-white/[0.06] bg-card dark:bg-white/[0.02] px-3 py-2">
            <div className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <a
                  href={article.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-foreground dark:text-[#f7f8f8] hover:text-[#5e6ad2] dark:hover:text-[#a5b4fc] transition-colors line-clamp-1"
                  style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                >
                  {article.title}
                </a>
                <div className="flex items-center gap-2 mt-0.5 text-muted-foreground dark:text-[#62666d]" style={{ fontSize: '11px', letterSpacing: '-0.05px' }}>
                  {article.source_name && <span>{article.source_name}</span>}
                  {article.published_at && (
                    <span className="tabular-nums">
                      {new Date(article.published_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
                {article.description && (
                  <p className="text-muted-foreground dark:text-[#8a8f98] mt-1 line-clamp-2" style={{ fontSize: '12px', letterSpacing: '-0.1px', lineHeight: 1.5 }}>{article.description}</p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// PDL and Explorium panels removed — data now merged into unified Research view

// ---------------------------------------------------------------------------
// Tab Props
// ---------------------------------------------------------------------------

interface TabProps {
  contact: Contact;
  enrichmentData: any;
  enrichmentResults: Record<string, any>;
}

// ---------------------------------------------------------------------------
// ReachOutTab
// ---------------------------------------------------------------------------

function ReachOutTab({ contact, enrichmentData }: TabProps) {
  const [showCompose, setShowCompose] = useState(false);
  const buyingSignals = enrichmentData?.buying_signals || {};
  const outreachHooks = enrichmentData?.outreach_hooks || {};
  const communicationPrefs = enrichmentData?.communication_prefs || {};
  const relationshipMap = enrichmentData?.relationship_map || {};

  // Every enrichment section below is individually gated, so a contact with no
  // enrichment (or a completed-but-empty payload) used to render a blank tab.
  // Track whether anything can render to drive the fallback hint.
  const hasInsights = Boolean(
    buyingSignals.decision_authority?.buying_role ||
    buyingSignals.decision_authority?.score != null ||
    relationshipMap.champion_score?.score != null ||
    outreachHooks.icebreakers?.[0] ||
    buyingSignals.pain_points?.[0] ||
    communicationPrefs.preferred_style?.tone ||
    communicationPrefs.best_contact_time?.best_hours ||
    outreachHooks.scored_company_news?.length > 0 ||
    outreachHooks.role_changes?.length > 0
  );

  // The actual "reach out" action. It only needs an email address, so it is
  // never gated on enrichment. Prefill the mail draft with the best icebreaker
  // when we have one — that ties the AI insights to a concrete next step.
  const firstName =
    contact.first_name || (contact.full_name || '').trim().split(/\s+/)[0] || 'there';
  const bestIcebreaker: string | undefined = outreachHooks.icebreakers?.[0];
  const mailSubject = `Reaching out${contact.company_name ? ` — ${contact.company_name}` : ''}`;
  const mailBody = bestIcebreaker
    ? `Hi ${firstName},\n\n${bestIcebreaker}\n\n`
    : `Hi ${firstName},\n\n`;

  const scoreColor = (s: number) =>
    s >= 7 ? 'text-emerald-600 dark:text-emerald-400'
    : 'text-[#5e6ad2] dark:text-[#a5b4fc]';

  return (
    <div className="space-y-4">
      <ComposeEmailModal
        isOpen={showCompose}
        onClose={() => setShowCompose(false)}
        contact={contact}
        prefillSubject={mailSubject}
        prefillBody={mailBody}
      />

      {/* Reach-out action — always available (needs only an email address) */}
      <div className="rounded-lg border border-[#5e6ad2]/25 dark:border-[#5e6ad2]/30 bg-[#5e6ad2]/[0.06] dark:bg-[#5e6ad2]/[0.10] px-4 py-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 mb-0.5">
              <Lightning className="h-3.5 w-3.5 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="fill" />
              <span className="text-[#5e6ad2] dark:text-[#a5b4fc] uppercase tracking-[0.06em]" style={{ fontSize: '10px', fontWeight: 590 }}>Reach Out</span>
            </div>
            <p className="text-foreground dark:text-[#f7f8f8] truncate" style={{ fontSize: '13px', letterSpacing: '-0.13px' }}>
              {contact.email || 'No email address on file'}
            </p>
          </div>
          <Button
            variant="default"
            size="sm"
            disabled={!contact.email}
            onClick={() => setShowCompose(true)}
            icon={<PaperPlaneTilt className="h-3.5 w-3.5" weight="fill" />}
            className="flex-shrink-0"
          >
            Send Email
          </Button>
        </div>
        {bestIcebreaker && (
          <p className="text-muted-foreground dark:text-[#8a8f98] mt-2 pt-2 border-t border-[#5e6ad2]/15 dark:border-[#5e6ad2]/20" style={{ fontSize: '11px', letterSpacing: '-0.05px', lineHeight: 1.5 }}>
            Draft is prefilled with the best icebreaker below — edit before sending.
          </p>
        )}
      </div>

      {/* Fallback when there are no AI insights to render (blank-tab fix) */}
      {!hasInsights && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="h-12 w-12 rounded-md bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center mb-4">
            <Lightning className="h-5 w-5 text-muted-foreground dark:text-[#8a8f98]" />
          </div>
          <h3 className="text-foreground dark:text-[#f7f8f8] mb-1" style={{ fontSize: '15px', fontWeight: 590, letterSpacing: '-0.182px' }}>No outreach insights yet</h3>
          <p className="text-muted-foreground dark:text-[#8a8f98] max-w-xs" style={{ fontSize: '13px', letterSpacing: '-0.13px' }}>Enrich this contact to get icebreakers, hooks, and communication preferences.</p>
        </div>
      )}

      {/* Prospect Snapshot — compact inline bar */}
      {(buyingSignals.decision_authority?.buying_role || buyingSignals.decision_authority?.score != null || relationshipMap.champion_score?.score != null) && (
        <div className="flex items-center gap-2 flex-wrap bg-[#5e6ad2]/[0.06] dark:bg-[#5e6ad2]/[0.10] border border-[#5e6ad2]/20 dark:border-[#5e6ad2]/25 rounded-lg px-3 py-2">
          <Sparkle className="h-3.5 w-3.5 text-[#5e6ad2] dark:text-[#a5b4fc] flex-shrink-0" weight="fill" />
          {buyingSignals.decision_authority?.buying_role && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-background dark:bg-white/[0.04] border border-border/50 dark:border-white/[0.06] text-foreground dark:text-[#f7f8f8] capitalize" style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}>
              {buyingSignals.decision_authority.buying_role.replace(/_/g, ' ')}
            </span>
          )}
          {buyingSignals.decision_authority?.score != null && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-background dark:bg-white/[0.04] border border-border/50 dark:border-white/[0.06] text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}>
              Decision <span className={cn('tabular-nums', scoreColor(buyingSignals.decision_authority.score))} style={{ fontWeight: 590 }}>{buyingSignals.decision_authority.score}</span><span className="text-muted-foreground dark:text-[#62666d]">/10</span>
            </span>
          )}
          {relationshipMap.champion_score?.score != null && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-background dark:bg-white/[0.04] border border-border/50 dark:border-white/[0.06] text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}>
              Champion <span className={cn('tabular-nums', scoreColor(relationshipMap.champion_score.score))} style={{ fontWeight: 590 }}>{relationshipMap.champion_score.score}</span><span className="text-muted-foreground dark:text-[#62666d]">/10</span>
            </span>
          )}
        </div>
      )}

      {/* Best Icebreaker — hero callout */}
      {outreachHooks.icebreakers?.[0] && (
        <div className="border border-[#5e6ad2]/25 dark:border-[#5e6ad2]/30 rounded-lg overflow-hidden bg-[#5e6ad2]/[0.06] dark:bg-[#5e6ad2]/[0.10]">
          <div className="px-4 py-3">
            <div className="flex items-center gap-1.5 mb-2">
              <Lightning className="h-3.5 w-3.5 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="fill" />
              <span className="text-[#5e6ad2] dark:text-[#a5b4fc] uppercase tracking-[0.06em]" style={{ fontSize: '10px', fontWeight: 590 }}>Best Icebreaker</span>
            </div>
            <p className="text-foreground dark:text-[#f7f8f8] leading-relaxed italic" style={{ fontSize: '14px', letterSpacing: '-0.13px' }}>
              &ldquo;{outreachHooks.icebreakers[0]}&rdquo;
            </p>
          </div>
        </div>
      )}

      {/* Top Pain Point + Comm Prefs — side by side */}
      {(buyingSignals.pain_points?.[0] || communicationPrefs.preferred_style?.tone || communicationPrefs.best_contact_time?.best_hours) && (
        <div className="grid grid-cols-2 gap-3">
          {/* Top Pain Point */}
          {buyingSignals.pain_points?.[0] && (
            <div className="rounded-lg border border-border/50 bg-card dark:border-white/[0.08] dark:bg-white/[0.02] p-3">
              <div className="flex items-center gap-1 mb-1.5">
                <Warning className="h-3 w-3 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="fill" />
                <span className="text-[#5e6ad2] dark:text-[#a5b4fc] uppercase tracking-[0.06em]" style={{ fontSize: '10px', fontWeight: 590 }}>Top Pain Point</span>
              </div>
              <p className="text-foreground dark:text-[#f7f8f8] leading-snug" style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}>{buyingSignals.pain_points[0].pain}</p>
              {buyingSignals.pain_points[0].evidence && (
                <p className="text-muted-foreground dark:text-[#8a8f98] mt-1 italic" style={{ fontSize: '11px', letterSpacing: '-0.05px' }}>{buyingSignals.pain_points[0].evidence}</p>
              )}
            </div>
          )}

          {/* Communication Preferences */}
          {(communicationPrefs.preferred_style?.tone || communicationPrefs.best_contact_time?.best_hours) && (
            <div className="rounded-lg border border-border/50 bg-card dark:border-white/[0.08] dark:bg-white/[0.02] p-3">
              <div className="flex items-center gap-1 mb-1.5">
                <User className="h-3 w-3 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="fill" />
                <span className="text-[#5e6ad2] dark:text-[#a5b4fc] uppercase tracking-[0.06em]" style={{ fontSize: '10px', fontWeight: 590 }}>How to Reach</span>
              </div>
              <div className="space-y-1.5">
                {communicationPrefs.preferred_style?.tone && (
                  <div className="flex items-center gap-1.5" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
                    <span className="text-muted-foreground dark:text-[#8a8f98]">Tone:</span>
                    <span className="text-foreground dark:text-[#f7f8f8] capitalize" style={{ fontWeight: 510 }}>{communicationPrefs.preferred_style.tone}</span>
                  </div>
                )}
                {communicationPrefs.best_contact_time?.best_hours && (
                  <div className="flex items-center gap-1.5" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
                    <span className="text-muted-foreground dark:text-[#8a8f98]">Best time:</span>
                    <span className="text-foreground dark:text-[#f7f8f8]" style={{ fontWeight: 510 }}>{communicationPrefs.best_contact_time.best_hours}</span>
                  </div>
                )}
                {communicationPrefs.content_preferences?.preferred_formats?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {communicationPrefs.content_preferences.preferred_formats.filter((f: any) => f).map((f: string, idx: number) => (
                      <span key={idx} className="px-1.5 py-0.5 rounded-md border bg-[#5e6ad2]/[0.10] text-[#5e6ad2] border-[#5e6ad2]/25 dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc] dark:border-[#5e6ad2]/30 capitalize" style={{ fontSize: '10px', fontWeight: 510 }}>
                        {f.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* All Icebreakers */}
      {outreachHooks.icebreakers && outreachHooks.icebreakers.length > 1 && (
        <div className="border border-border/50 dark:border-white/[0.08] dark:bg-white/[0.02] rounded-lg overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3 border-b border-border/50 dark:border-white/[0.06] bg-muted/40 dark:bg-white/[0.03]">
            <Lightning className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="fill" />
            <span className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px', fontWeight: 590, letterSpacing: '-0.13px' }}>Icebreakers</span>
            <span className="ml-auto text-[#5e6ad2] dark:text-[#a5b4fc] bg-[#5e6ad2]/[0.10] dark:bg-[#5e6ad2]/[0.14] border border-[#5e6ad2]/25 dark:border-[#5e6ad2]/30 px-2 py-0.5 rounded-full tabular-nums" style={{ fontSize: '10px', fontWeight: 590 }}>
              {outreachHooks.icebreakers.length}
            </span>
          </div>
          <div className="p-3 space-y-1.5">
            {outreachHooks.icebreakers.map((icebreaker: string, idx: number) => (
              <div key={idx} className={cn(
                'flex items-start gap-2.5 rounded-md px-3 py-2',
                idx === 0
                  ? 'bg-[#5e6ad2]/[0.06] dark:bg-[#5e6ad2]/[0.10] border border-[#5e6ad2]/20 dark:border-[#5e6ad2]/25'
                  : 'bg-muted/30 dark:bg-white/[0.02]'
              )}>
                <span className={cn(
                  'flex-shrink-0 h-5 w-5 rounded-full flex items-center justify-center tabular-nums mt-0.5',
                  idx === 0
                    ? 'bg-[#5e6ad2]/[0.18] dark:bg-[#5e6ad2]/[0.25] text-[#5e6ad2] dark:text-[#a5b4fc]'
                    : 'bg-muted/60 dark:bg-white/[0.06] text-muted-foreground dark:text-[#8a8f98]'
                )} style={{ fontSize: '10px', fontWeight: 590 }}>
                  {idx + 1}
                </span>
                <p className="text-foreground/90 dark:text-[#d0d6e0] italic leading-snug" style={{ fontSize: '13px', letterSpacing: '-0.13px' }}>&ldquo;{icebreaker}&rdquo;</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Hooks — scored news + role changes */}
      {(outreachHooks.scored_company_news?.length > 0 || outreachHooks.role_changes?.length > 0) && (
        <div className="border border-border/50 dark:border-white/[0.08] dark:bg-white/[0.02] rounded-lg overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3 border-b border-border/50 dark:border-white/[0.06] bg-muted/40 dark:bg-white/[0.03]">
            <TrendUp className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc]" />
            <span className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px', fontWeight: 590, letterSpacing: '-0.13px' }}>Outreach Hooks</span>
          </div>
          <div className="p-3 space-y-3">
            {/* Scored News */}
            {outreachHooks.scored_company_news && outreachHooks.scored_company_news.length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <Newspaper className="h-3.5 w-3.5 text-muted-foreground dark:text-[#8a8f98]" />
                  <span className="text-foreground dark:text-[#f7f8f8] uppercase tracking-[0.06em]" style={{ fontSize: '11px', fontWeight: 590 }}>Company News</span>
                </div>
                <div className="space-y-1.5">
                  {outreachHooks.scored_company_news.filter((x: any) => x && typeof x === 'object').map((news: any, idx: number) => (
                    <div key={idx} className="rounded-md border border-border/50 dark:border-white/[0.06] bg-card dark:bg-white/[0.02] hover:bg-muted/20 dark:hover:bg-white/[0.03] transition-colors overflow-hidden">
                      <div className="flex items-stretch">
                        <div className="flex-1 px-3 py-2 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-foreground dark:text-[#f7f8f8] leading-snug flex-1" style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}>{news.headline}</p>
                            {news.hook_score != null && (
                              <span className={cn(
                                'flex-shrink-0 px-1.5 py-0.5 rounded-md border tabular-nums',
                                news.hook_score >= 8 ? 'bg-emerald-500/10 border-emerald-500/25 text-emerald-700 dark:bg-emerald-500/[0.12] dark:border-emerald-500/30 dark:text-emerald-300' :
                                news.hook_score >= 5 ? 'bg-amber-500/10 border-amber-500/25 text-amber-700 dark:bg-amber-500/[0.12] dark:border-amber-500/30 dark:text-amber-300' :
                                'bg-muted/50 border-border/50 text-muted-foreground dark:bg-white/[0.04] dark:border-white/[0.06] dark:text-[#8a8f98]'
                              )} style={{ fontSize: '10px', fontWeight: 590 }}>
                                {news.hook_score}/10
                              </span>
                            )}
                          </div>
                          {news.summary && <p className="text-muted-foreground dark:text-[#8a8f98] mt-0.5" style={{ fontSize: '11px', letterSpacing: '-0.05px', lineHeight: 1.5 }}>{news.summary}</p>}
                          {news.hook_angle && (
                            <div className="flex items-center gap-1 mt-1">
                              <Lightning className="h-2.5 w-2.5 text-[#5e6ad2] dark:text-[#a5b4fc] flex-shrink-0" weight="fill" />
                              <p className="text-[#5e6ad2] dark:text-[#a5b4fc]" style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.05px' }}>{news.hook_angle}</p>
                            </div>
                          )}
                          <SourceCitation source={news.source} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Role Changes */}
            {outreachHooks.role_changes && outreachHooks.role_changes.length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <BriefcasePhosphor className="h-3.5 w-3.5 text-muted-foreground dark:text-[#8a8f98]" />
                  <span className="text-foreground dark:text-[#f7f8f8] uppercase tracking-[0.06em]" style={{ fontSize: '11px', fontWeight: 590 }}>Role Changes</span>
                </div>
                <div className="space-y-1.5">
                  {outreachHooks.role_changes.filter((x: any) => x && typeof x === 'object').map((rc: any, idx: number) => (
                    <div key={idx} className="flex items-start gap-2.5 bg-muted/30 dark:bg-white/[0.02] border border-border/50 dark:border-white/[0.06] rounded-md px-3 py-2">
                      <div className="h-5 w-5 rounded-full bg-[#5e6ad2]/[0.10] dark:bg-[#5e6ad2]/[0.14] border border-[#5e6ad2]/20 dark:border-[#5e6ad2]/25 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <BriefcasePhosphor className="h-2.5 w-2.5 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="bold" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>{rc.description}</p>
                        {rc.approximate_date && <p className="text-muted-foreground dark:text-[#62666d] mt-0.5 tabular-nums" style={{ fontSize: '10px' }}>{rc.approximate_date}</p>}
                        <SourceCitation source={rc.source} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// EmailsTab
// ---------------------------------------------------------------------------

function EmailsTab({ contact }: TabProps) {
  const sent = contact.total_emails_sent || 0;
  const opened = contact.total_emails_opened || 0;
  const clicked = contact.total_emails_clicked || 0;
  const replied = contact.total_emails_replied || 0;

  const openRate = sent > 0 ? Math.round((opened / sent) * 100) : 0;
  const clickRate = sent > 0 ? Math.round((clicked / sent) * 100) : 0;
  const replyRate = sent > 0 ? Math.round((replied / sent) * 100) : 0;

  if (sent === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="h-12 w-12 rounded-md bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center mb-4">
          <Envelope className="h-5 w-5 text-muted-foreground dark:text-[#8a8f98]" />
        </div>
        <h3 className="text-foreground dark:text-[#f7f8f8] mb-1" style={{ fontSize: '15px', fontWeight: 590, letterSpacing: '-0.182px' }}>No emails sent yet</h3>
        <p className="text-muted-foreground dark:text-[#8a8f98] max-w-xs" style={{ fontSize: '13px', letterSpacing: '-0.13px' }}>
          Once this contact is included in a campaign, email engagement data will appear here.
        </p>
      </div>
    );
  }

  const TILE = 'rounded-lg p-4 border';
  const EYEBROW = 'text-muted-foreground dark:text-[#8a8f98] uppercase tracking-[0.06em]';
  const eyebrowStyle = { fontSize: '10px', fontWeight: 590 } as const;
  const numStyle = { fontSize: '22px', fontWeight: 590, letterSpacing: '-0.24px', lineHeight: 1.1 } as const;

  return (
    <div className="space-y-5">
      {/* Engagement Overview */}
      <div className="grid grid-cols-4 gap-3">
        <div className={cn(TILE, 'border-border/50 bg-muted/40 dark:border-white/[0.06] dark:bg-white/[0.02]')}>
          <div className="flex items-center gap-2 mb-2">
            <PaperPlaneTilt className="h-4 w-4 text-muted-foreground dark:text-[#8a8f98]" />
            <span className={EYEBROW} style={eyebrowStyle}>Sent</span>
          </div>
          <p className="text-foreground dark:text-[#f7f8f8] tabular-nums" style={numStyle}>{sent}</p>
        </div>
        <div className={cn(TILE, 'border-border/50 bg-muted/40 dark:border-white/[0.06] dark:bg-white/[0.02]')}>
          <div className="flex items-center gap-2 mb-2">
            <EnvelopeOpen className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc]" />
            <span className={EYEBROW} style={eyebrowStyle}>Opened</span>
          </div>
          <p className="text-[#5e6ad2] dark:text-[#a5b4fc] tabular-nums" style={numStyle}>{opened}</p>
          <p className="text-muted-foreground dark:text-[#8a8f98] mt-0.5 tabular-nums" style={{ fontSize: '11px', letterSpacing: '-0.05px' }}>{openRate}% rate</p>
        </div>
        <div className={cn(TILE, 'border-border/50 bg-muted/40 dark:border-white/[0.06] dark:bg-white/[0.02]')}>
          <div className="flex items-center gap-2 mb-2">
            <CursorClick className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc]" />
            <span className={EYEBROW} style={eyebrowStyle}>Clicked</span>
          </div>
          <p className="text-[#5e6ad2] dark:text-[#a5b4fc] tabular-nums" style={numStyle}>{clicked}</p>
          <p className="text-muted-foreground dark:text-[#8a8f98] mt-0.5 tabular-nums" style={{ fontSize: '11px', letterSpacing: '-0.05px' }}>{clickRate}% rate</p>
        </div>
        <div className={cn(TILE, 'border-border/50 bg-muted/40 dark:border-white/[0.06] dark:bg-white/[0.02]')}>
          <div className="flex items-center gap-2 mb-2">
            <ArrowBendUpLeft className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc]" />
            <span className={EYEBROW} style={eyebrowStyle}>Replied</span>
          </div>
          <p className="text-[#5e6ad2] dark:text-[#a5b4fc] tabular-nums" style={numStyle}>{replied}</p>
          <p className="text-muted-foreground dark:text-[#8a8f98] mt-0.5 tabular-nums" style={{ fontSize: '11px', letterSpacing: '-0.05px' }}>{replyRate}% rate</p>
        </div>
      </div>

      {/* Engagement Funnel */}
      <div className="border border-border/50 dark:border-white/[0.08] dark:bg-white/[0.02] rounded-lg overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border/50 dark:border-white/[0.06] bg-muted/40 dark:bg-white/[0.03]">
          <ChartBar className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc]" />
          <span className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px', fontWeight: 590, letterSpacing: '-0.13px' }}>Engagement Funnel</span>
        </div>
        <div className="p-4 space-y-3">
          {[
            { label: 'Sent', value: sent, pct: 100, color: 'bg-foreground/30 dark:bg-white/30', textColor: 'text-foreground dark:text-[#f7f8f8]' },
            { label: 'Opened', value: opened, pct: openRate, color: 'bg-[#5e6ad2] dark:bg-[#a5b4fc]', textColor: 'text-[#5e6ad2] dark:text-[#a5b4fc]' },
            { label: 'Clicked', value: clicked, pct: clickRate, color: 'bg-emerald-500 dark:bg-emerald-400', textColor: 'text-emerald-700 dark:text-emerald-300' },
            { label: 'Replied', value: replied, pct: replyRate, color: 'bg-[#5e6ad2] dark:bg-[#a5b4fc]', textColor: 'text-[#5e6ad2] dark:text-[#a5b4fc]' },
          ].map((row) => (
            <div key={row.label} className="space-y-1">
              <div className="flex items-center justify-between" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
                <span className="text-muted-foreground dark:text-[#8a8f98]">{row.label}</span>
                <span className={cn('tabular-nums', row.textColor)} style={{ fontWeight: 510 }}>
                  {row.value}{row.label !== 'Sent' && ` (${row.pct}%)`}
                </span>
              </div>
              <div className="h-2 bg-muted/40 dark:bg-white/[0.05] rounded-full overflow-hidden">
                <div className={cn('h-full rounded-full transition-all', row.color)} style={{ width: `${row.pct}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Last Email Info */}
      {contact.last_email_sent && (
        <div className="border border-border/50 dark:border-white/[0.08] dark:bg-white/[0.02] rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <ClockPhosphor className="h-4 w-4 text-muted-foreground dark:text-[#8a8f98]" />
            <span className={EYEBROW} style={eyebrowStyle}>Last Email Sent</span>
          </div>
          <p className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}>
            {new Date(contact.last_email_sent).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
          <p className="text-muted-foreground dark:text-[#8a8f98] mt-0.5 tabular-nums" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
            {new Date(contact.last_email_sent).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
      )}

      {/* Email Status */}
      <div className="border border-border/50 dark:border-white/[0.08] dark:bg-white/[0.02] rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="h-4 w-4 text-muted-foreground dark:text-[#8a8f98]" />
          <span className={EYEBROW} style={eyebrowStyle}>Deliverability</span>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <span className={cn(
            'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border capitalize',
            contact.email_status === 'active' ? 'bg-emerald-500/10 text-emerald-700 border-emerald-500/25 dark:bg-emerald-500/[0.12] dark:text-emerald-300 dark:border-emerald-500/25' :
            (contact.email_status === 'bounced' || contact.email_status === 'invalid') ? 'bg-red-500/10 text-red-700 border-red-500/25 dark:bg-red-500/[0.12] dark:text-red-300 dark:border-red-500/25' :
            'bg-amber-500/10 text-amber-700 border-amber-500/25 dark:bg-amber-500/[0.12] dark:text-amber-300 dark:border-amber-500/25'
          )} style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}>
            {contact.email_status === 'active' && <CheckCircle className="h-3.5 w-3.5" weight="fill" />}
            {(contact.email_status === 'bounced' || contact.email_status === 'invalid') && <XCirclePhosphor className="h-3.5 w-3.5" weight="fill" />}
            {contact.email_status !== 'active' && contact.email_status !== 'bounced' && contact.email_status !== 'invalid' && <Warning className="h-3.5 w-3.5" weight="fill" />}
            {contact.email_status === 'invalid' ? 'Invalid' : (contact.email_status || 'Unknown')}
          </span>
          {contact.unsubscribed_at && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border bg-red-500/10 text-red-700 border-red-500/25 dark:bg-red-500/[0.12] dark:text-red-300 dark:border-red-500/25" style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}>
              <XCirclePhosphor className="h-3.5 w-3.5" weight="fill" />
              Unsubscribed <span className="tabular-nums">{formatDate(contact.unsubscribed_at, 'PP')}</span>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ActivityTab
// ---------------------------------------------------------------------------

function ActivityTab({ contact }: TabProps) {
  // Build activity timeline from available contact data
  const events: { date: string; type: string; icon: React.ReactNode; title: string; detail?: string; color: string }[] = [];

  if (contact.created_at) {
    events.push({
      date: contact.created_at,
      type: 'created',
      icon: <User className="h-3.5 w-3.5" />,
      title: 'Contact created',
      detail: contact.import_source ? `Imported via ${contact.import_source}` : 'Added manually',
      color: 'text-[#5e6ad2] bg-[#5e6ad2]/[0.10] border border-[#5e6ad2]/25 dark:text-[#a5b4fc] dark:bg-[#5e6ad2]/[0.14] dark:border-[#5e6ad2]/30',
    });
  }

  if (contact.last_enriched_at) {
    events.push({
      date: contact.last_enriched_at,
      type: 'enriched',
      icon: <Sparkle className="h-3.5 w-3.5" weight="fill" />,
      title: 'Contact enriched',
      detail: [
        contact.enrichment_provider && `Provider: ${contact.enrichment_provider}`,
        contact.enrichment_model && `Model: ${contact.enrichment_model}`,
      ].filter(Boolean).join(' · ') || undefined,
      color: 'text-[#5e6ad2] bg-[#5e6ad2]/[0.10] border border-[#5e6ad2]/25 dark:text-[#a5b4fc] dark:bg-[#5e6ad2]/[0.14] dark:border-[#5e6ad2]/30',
    });
  }

  if (contact.last_email_sent) {
    events.push({
      date: contact.last_email_sent,
      type: 'email_sent',
      icon: <PaperPlaneTilt className="h-3.5 w-3.5" />,
      title: 'Last email sent',
      detail: `${contact.total_emails_sent || 0} total emails sent`,
      color: 'text-emerald-700 bg-emerald-500/[0.10] border border-emerald-500/25 dark:text-emerald-300 dark:bg-emerald-500/[0.14] dark:border-emerald-500/30',
    });
  }

  if (contact.unsubscribed_at) {
    events.push({
      date: contact.unsubscribed_at,
      type: 'unsubscribed',
      icon: <XCirclePhosphor className="h-3.5 w-3.5" weight="fill" />,
      title: 'Contact unsubscribed',
      color: 'text-red-700 bg-red-500/[0.10] border border-red-500/25 dark:text-red-300 dark:bg-red-500/[0.14] dark:border-red-500/30',
    });
  }

  if (contact.updated_at && contact.updated_at !== contact.created_at) {
    events.push({
      date: contact.updated_at,
      type: 'updated',
      icon: <PencilSimple className="h-3.5 w-3.5" />,
      title: 'Contact updated',
      color: 'text-amber-700 bg-amber-500/[0.10] border border-amber-500/25 dark:text-amber-300 dark:bg-amber-500/[0.14] dark:border-amber-500/30',
    });
  }

  // Sort descending (most recent first)
  events.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  if (events.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="h-12 w-12 rounded-md bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center mb-4">
          <ClockPhosphor className="h-5 w-5 text-muted-foreground dark:text-[#8a8f98]" />
        </div>
        <h3 className="text-foreground dark:text-[#f7f8f8] mb-1" style={{ fontSize: '15px', fontWeight: 590, letterSpacing: '-0.182px' }}>No activity yet</h3>
        <p className="text-muted-foreground dark:text-[#8a8f98] max-w-xs" style={{ fontSize: '13px', letterSpacing: '-0.13px' }}>
          Activity will appear here as you interact with this contact.
        </p>
      </div>
    );
  }

  const enrichTone =
    contact.enrichment_status === 'completed' ? 'text-emerald-700 dark:text-emerald-300' :
    contact.enrichment_status === 'in_progress' ? 'text-[#5e6ad2] dark:text-[#a5b4fc]' :
    contact.enrichment_status === 'failed' ? 'text-red-700 dark:text-red-300' :
    'text-muted-foreground dark:text-[#8a8f98]';

  // Why the last run fell short, classified by the API. The status tile alone
  // said "failed" for everything -- a rejected key, a provider with nothing to
  // search with, a run that never finished -- so nobody could tell whether to
  // go and change an API key.
  const problems = contact.enrichment_problems ?? [];
  const problemTone_ = problemTone(problems);
  const headline = headlineMessage(problems);

  const SUMMARY_TILE = 'rounded-lg p-4 border border-border/50 bg-muted/40 dark:bg-white/[0.02] dark:border-white/[0.08]';
  const EYEBROW = 'text-muted-foreground dark:text-[#8a8f98] uppercase tracking-[0.06em]';
  const eyebrowStyle = { fontSize: '10px', fontWeight: 590 } as const;

  return (
    <div className="space-y-5">
      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className={SUMMARY_TILE}>
          <p className={EYEBROW} style={eyebrowStyle}>Lead Status</p>
          <p className="text-foreground dark:text-[#f7f8f8] mt-1 capitalize" style={{ fontSize: '14px', fontWeight: 590, letterSpacing: '-0.13px' }}>{contact.lead_status || 'New'}</p>
        </div>
        <div className={SUMMARY_TILE}>
          <p className={EYEBROW} style={eyebrowStyle}>Source</p>
          <p className="text-foreground dark:text-[#f7f8f8] mt-1 capitalize" style={{ fontSize: '14px', fontWeight: 590, letterSpacing: '-0.13px' }}>{contact.import_source || 'Manual'}</p>
        </div>
        <div className={SUMMARY_TILE}>
          <p className={EYEBROW} style={eyebrowStyle}>Enrichment</p>
          <p className={cn('mt-1 capitalize', enrichTone)} style={{ fontSize: '14px', fontWeight: 590, letterSpacing: '-0.13px' }}>
            {contact.enrichment_status || 'Not enriched'}
          </p>
        </div>
      </div>

      {/* Why the run fell short. Tone is deliberate: an informational run (every
          provider simply had nothing to search with) must not be painted red --
          that is what made a contact with no company website look broken. */}
      {headline && problemTone_ && (
        <div
          role={problemTone_ === 'info' ? undefined : 'alert'}
          className={cn(
            'rounded-lg border p-3',
            problemTone_ === 'action'
              ? 'border-amber-500/30 bg-amber-500/10 dark:bg-amber-500/[0.10]'
              : problemTone_ === 'warning'
                ? 'border-red-500/25 bg-red-500/[0.07] dark:bg-red-500/[0.10]'
                : 'border-border/50 bg-muted/40 dark:bg-white/[0.02] dark:border-white/[0.08]',
          )}
        >
          <p
            className={cn(
              problemTone_ === 'action'
                ? 'text-amber-800 dark:text-amber-300'
                : problemTone_ === 'warning'
                  ? 'text-red-700 dark:text-red-300'
                  : 'text-muted-foreground dark:text-[#8a8f98]',
            )}
            style={{ fontSize: '12px', letterSpacing: '-0.08px', lineHeight: 1.5 }}
          >
            {headline}
          </p>
          {problems.length > 1 && (
            <ul className="mt-2 space-y-1">
              {problems.map((p, i) => (
                <li
                  key={`${p.provider}-${i}`}
                  className="text-muted-foreground dark:text-[#8a8f98]"
                  style={{ fontSize: '11px', letterSpacing: '-0.05px' }}
                  // The raw vendor text on hover, for whoever needs it.
                  title={p.error || undefined}
                >
                  <span className="text-foreground dark:text-[#f7f8f8]">{providerLabel(p.provider)}</span>
                  {': '}{p.hint}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Timeline */}
      <div className="border border-border/50 dark:border-white/[0.08] dark:bg-white/[0.02] rounded-lg overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border/50 dark:border-white/[0.06] bg-muted/40 dark:bg-white/[0.03]">
          <ClockPhosphor className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc]" />
          <span className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px', fontWeight: 590, letterSpacing: '-0.13px' }}>Timeline</span>
        </div>
        <div className="p-4">
          <div className="relative">
            <div className="space-y-4">
              {events.map((event, idx) => (
                <div key={idx} className="flex items-start gap-3 relative">
                  {/* Connector — runs from this circle's bottom to the next circle's top,
                      so it sits in the gap and never crosses the circles. */}
                  {idx < events.length - 1 && (
                    <span className="absolute left-[15px] top-[30px] -bottom-4 w-px bg-border/60 dark:bg-white/[0.06]" aria-hidden="true" />
                  )}
                  {/* Icon circle */}
                  <div className={cn(
                    'relative flex-shrink-0 h-[30px] w-[30px] rounded-full flex items-center justify-center z-10',
                    event.color
                  )}>
                    {event.icon}
                  </div>
                  {/* Content */}
                  <div className="flex-1 min-w-0 pt-0.5">
                    <p className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}>{event.title}</p>
                    {event.detail && (
                      <p className="text-muted-foreground dark:text-[#8a8f98] mt-0.5" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>{event.detail}</p>
                    )}
                    <p className="text-muted-foreground dark:text-[#62666d] mt-1 tabular-nums" style={{ fontSize: '10px' }}>
                      {new Date(event.date).toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}
                      {' '}at{' '}
                      {new Date(event.date).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Tags */}
      {contact.tags && contact.tags.length > 0 && (
        <div className="border border-border/50 dark:border-white/[0.08] dark:bg-white/[0.02] rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <TargetPhosphor className="h-4 w-4 text-muted-foreground dark:text-[#8a8f98]" />
            <span className={EYEBROW} style={eyebrowStyle}>Tags</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {contact.tags.map((tag, idx) => (
              <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded-full border bg-[#5e6ad2]/[0.10] text-[#5e6ad2] border-[#5e6ad2]/25 dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc] dark:border-[#5e6ad2]/30" style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}>
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Segments */}
      {contact.segments && contact.segments.length > 0 && (
        <div className="border border-border/50 dark:border-white/[0.08] dark:bg-white/[0.02] rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <UsersThree className="h-4 w-4 text-muted-foreground dark:text-[#8a8f98]" />
            <span className={EYEBROW} style={eyebrowStyle}>Segments</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {contact.segments.map((segment, idx) => (
              <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded-full border bg-muted/60 text-muted-foreground border-border/50 dark:bg-white/[0.04] dark:text-[#d0d6e0] dark:border-white/[0.06]" style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}>
                {segment}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// QualifyTab
// ---------------------------------------------------------------------------

function QualifyTab({ enrichmentData }: TabProps) {
  if (!enrichmentData) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="h-12 w-12 rounded-md bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center mb-4">
          <TargetPhosphor className="h-5 w-5 text-muted-foreground dark:text-[#8a8f98]" />
        </div>
        <h3 className="text-foreground dark:text-[#f7f8f8] mb-1" style={{ fontSize: '15px', fontWeight: 590, letterSpacing: '-0.182px' }}>No qualification data</h3>
        <p className="text-muted-foreground dark:text-[#8a8f98] max-w-xs" style={{ fontSize: '13px', letterSpacing: '-0.13px' }}>Enrich this contact to see scores, pain points, and competitive intelligence.</p>
      </div>
    );
  }

  const buyingSignals = enrichmentData?.buying_signals || {};
  const competitiveIntel = enrichmentData?.competitive_intel || {};
  const relationshipMap = enrichmentData?.relationship_map || {};

  const decisionScore = buyingSignals.decision_authority?.score;
  const championScore = relationshipMap.champion_score?.score;

  const scoreColor = (s: number | null | undefined) => {
    if (s == null) return 'text-muted-foreground dark:text-[#8a8f98]';
    if (s >= 7) return 'text-emerald-600 dark:text-emerald-400';
    return 'text-[#5e6ad2] dark:text-[#a5b4fc]';
  };
  const scoreBg = (s: number | null | undefined) => {
    if (s == null) return 'bg-muted/40 dark:bg-white/[0.02]';
    return 'bg-card dark:bg-white/[0.02]';
  };
  const scoreBorder = (s: number | null | undefined) => {
    if (s == null) return 'border-border/50 dark:border-white/[0.08]';
    return 'border-border/50 dark:border-white/[0.08]';
  };
  const scoreRingTrack = (s: number | null | undefined) => {
    if (s == null) return 'stroke-muted/40 dark:stroke-white/[0.08]';
    if (s >= 7) return 'stroke-emerald-500/15 dark:stroke-emerald-400/15';
    return 'stroke-[#5e6ad2]/15 dark:stroke-[#a5b4fc]/15';
  };
  const scoreRingFill = (s: number | null | undefined) => {
    if (s == null) return 'stroke-muted-foreground/40 dark:stroke-white/[0.20]';
    if (s >= 7) return 'stroke-emerald-500 dark:stroke-emerald-400';
    return 'stroke-[#5e6ad2] dark:stroke-[#a5b4fc]';
  };

  // Ring SVG helper
  const ScoreRing = ({ score, size = 56 }: { score: number | null | undefined; size?: number }) => {
    const r = (size - 6) / 2;
    const circumference = 2 * Math.PI * r;
    const pct = score != null ? score / 10 : 0;
    return (
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={4}
          className={scoreRingTrack(score)} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={4}
          className={scoreRingFill(score)}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - pct)}
          style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
      </svg>
    );
  };

  const TILE = 'rounded-lg p-4 border';
  const NEUTRAL_TILE = cn(TILE, 'border-border/50 bg-muted/40 dark:border-white/[0.08] dark:bg-white/[0.02]');
  const EYEBROW = 'text-muted-foreground dark:text-[#8a8f98] uppercase tracking-[0.06em]';
  const eyebrowStyle = { fontSize: '10px', fontWeight: 590 } as const;

  return (
    <div className="space-y-6">
      {/* Score Cards — with ring gauges */}
      <div className="grid grid-cols-2 gap-3">
        {/* Decision Power */}
        <div className={cn(TILE, scoreBg(decisionScore), scoreBorder(decisionScore))}>
          <div className="flex items-center gap-4">
            <div className="relative flex-shrink-0">
              <ScoreRing score={decisionScore} />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className={cn('tabular-nums', scoreColor(decisionScore))} style={{ fontSize: '18px', fontWeight: 590, letterSpacing: '-0.182px' }}>
                  {decisionScore ?? '--'}
                </span>
              </div>
            </div>
            <div>
              <p className={EYEBROW} style={eyebrowStyle}>Decision Power</p>
              <p className="text-muted-foreground dark:text-[#8a8f98] mt-0.5" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
                {decisionScore != null
                  ? decisionScore >= 7 ? 'Strong authority' : decisionScore >= 4 ? 'Moderate influence' : 'Limited power'
                  : 'Not assessed'}
              </p>
            </div>
          </div>
        </div>

        {/* Champion Score */}
        <div className={cn(TILE, scoreBg(championScore), scoreBorder(championScore))}>
          <div className="flex items-center gap-4">
            <div className="relative flex-shrink-0">
              <ScoreRing score={championScore} />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className={cn('tabular-nums', scoreColor(championScore))} style={{ fontSize: '18px', fontWeight: 590, letterSpacing: '-0.182px' }}>
                  {championScore ?? '--'}
                </span>
              </div>
            </div>
            <div>
              <p className={EYEBROW} style={eyebrowStyle}>Champion Score</p>
              <p className="text-muted-foreground dark:text-[#8a8f98] mt-0.5" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
                {championScore != null
                  ? championScore >= 7 ? 'Strong advocate' : championScore >= 4 ? 'Potential ally' : 'Unlikely champion'
                  : 'Not assessed'}
              </p>
              {relationshipMap.champion_score?.reasoning && (
                <p className="text-muted-foreground dark:text-[#62666d] mt-1 italic line-clamp-2" style={{ fontSize: '11px', letterSpacing: '-0.05px' }}>{relationshipMap.champion_score.reasoning}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Role & Budget row */}
      <div className="grid grid-cols-2 gap-3">
        <div className={NEUTRAL_TILE}>
          <div className="flex items-center gap-2 mb-2">
            <BriefcasePhosphor className="h-3.5 w-3.5 text-[#5e6ad2] dark:text-[#a5b4fc]" />
            <p className={EYEBROW} style={eyebrowStyle}>Buying Role</p>
          </div>
          <p className="text-foreground dark:text-[#f7f8f8] capitalize" style={{ fontSize: '14px', fontWeight: 590, letterSpacing: '-0.13px' }}>
            {buyingSignals.decision_authority?.buying_role?.replace(/_/g, ' ') || '--'}
          </p>
        </div>

        {enrichmentData?.person?.salary_estimate ? (
          <div className={NEUTRAL_TILE}>
            <div className="flex items-center gap-2 mb-2">
              <CurrencyDollar className="h-3.5 w-3.5 text-[#5e6ad2] dark:text-[#a5b4fc]" />
              <p className={EYEBROW} style={eyebrowStyle}>Est. Pay Band</p>
            </div>
            <p className="text-foreground dark:text-[#f7f8f8] tabular-nums" style={{ fontSize: '14px', fontWeight: 590, letterSpacing: '-0.13px' }}>
              ${Math.round((enrichmentData.person.salary_estimate.low || 0) / 1000)}K – ${Math.round((enrichmentData.person.salary_estimate.high || 0) / 1000)}K
            </p>
            {enrichmentData.person.salary_estimate.seniority_band && (
              <p className="text-muted-foreground dark:text-[#62666d] mt-0.5" style={{ fontSize: '11px', letterSpacing: '-0.05px' }}>{enrichmentData.person.salary_estimate.seniority_band}</p>
            )}
          </div>
        ) : (
          <div className={NEUTRAL_TILE}>
            <div className="flex items-center gap-2 mb-2">
              <CurrencyDollar className="h-3.5 w-3.5 text-[#5e6ad2] dark:text-[#a5b4fc]" />
              <p className={EYEBROW} style={eyebrowStyle}>Est. Pay Band</p>
            </div>
            <p className="text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '14px', fontWeight: 590, letterSpacing: '-0.13px' }}>--</p>
          </div>
        )}
      </div>

      {/* Pain Points */}
      {buyingSignals.pain_points && buyingSignals.pain_points.length > 0 && (
        <div className="border border-border/50 dark:border-white/[0.08] dark:bg-white/[0.02] rounded-lg overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3 border-b border-border/50 dark:border-white/[0.06] bg-muted/40 dark:bg-white/[0.03]">
            <Warning className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="fill" />
            <span className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px', fontWeight: 590, letterSpacing: '-0.13px' }}>Pain Points</span>
            <span className="ml-auto bg-[#5e6ad2]/10 text-[#5e6ad2] border border-[#5e6ad2]/25 dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc] dark:border-[#5e6ad2]/30 px-2 py-0.5 rounded-full tabular-nums" style={{ fontSize: '10px', fontWeight: 590 }}>
              {buyingSignals.pain_points.filter((x: any) => x && typeof x === 'object').length}
            </span>
          </div>
          <div className="p-4 space-y-2">
            {buyingSignals.pain_points.filter((x: any) => x && typeof x === 'object').map((pp: any, idx: number) => (
              <div key={idx} className="rounded-md border border-border/50 bg-card dark:border-white/[0.08] dark:bg-white/[0.02] p-3">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-foreground dark:text-[#f7f8f8] flex-1" style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}>{pp.pain}</p>
                  {pp.relevance_score != null && (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <div className="flex gap-0.5">
                        {Array.from({ length: 10 }).map((_, i) => (
                          <div key={i} className={cn(
                            'h-1.5 w-1.5 rounded-full',
                            i < pp.relevance_score
                              ? pp.relevance_score >= 8 ? 'bg-red-500 dark:bg-red-400' : pp.relevance_score >= 5 ? 'bg-amber-500 dark:bg-amber-400' : 'bg-muted-foreground/40 dark:bg-white/[0.20]'
                              : 'bg-muted/50 dark:bg-white/[0.06]'
                          )} />
                        ))}
                      </div>
                      <span className={cn('ml-1 tabular-nums',
                        pp.relevance_score >= 8 ? 'text-red-700 dark:text-red-300' : pp.relevance_score >= 5 ? 'text-amber-700 dark:text-amber-300' : 'text-muted-foreground dark:text-[#8a8f98]'
                      )} style={{ fontSize: '10px', fontWeight: 590 }}>
                        {pp.relevance_score}
                      </span>
                    </div>
                  )}
                </div>
                {pp.evidence && (
                  <p className="text-muted-foreground dark:text-[#8a8f98] mt-1.5 italic" style={{ fontSize: '12px', letterSpacing: '-0.1px', lineHeight: 1.5 }}>{pp.evidence}</p>
                )}
                <SourceCitation source={pp.source} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Budget & Timing */}
      {buyingSignals.budget_cycle && (
        <div className="border border-border/50 dark:border-white/[0.08] dark:bg-white/[0.02] rounded-lg overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3 border-b border-border/50 dark:border-white/[0.06] bg-muted/40 dark:bg-white/[0.03]">
            <CurrencyDollar className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc]" />
            <span className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px', fontWeight: 590, letterSpacing: '-0.13px' }}>Budget & Timing</span>
          </div>
          <div className="p-5">
            <div className="grid grid-cols-3 gap-3">
              {buyingSignals.budget_cycle.fiscal_year_end && (
                <div className={NEUTRAL_TILE}>
                  <p className={EYEBROW} style={eyebrowStyle}>Fiscal Year End</p>
                  <p className="text-foreground dark:text-[#f7f8f8] mt-0.5" style={{ fontSize: '14px', fontWeight: 590, letterSpacing: '-0.13px' }}>{buyingSignals.budget_cycle.fiscal_year_end}</p>
                </div>
              )}
              {buyingSignals.budget_cycle.planning_phase && (
                <div className={NEUTRAL_TILE}>
                  <p className={EYEBROW} style={eyebrowStyle}>Planning Phase</p>
                  <p className="text-foreground dark:text-[#f7f8f8] mt-0.5" style={{ fontSize: '14px', fontWeight: 590, letterSpacing: '-0.13px' }}>{buyingSignals.budget_cycle.planning_phase}</p>
                </div>
              )}
              {buyingSignals.budget_cycle.current_phase && (
                <div className={NEUTRAL_TILE}>
                  <p className={EYEBROW} style={eyebrowStyle}>Current Phase</p>
                  <p className="text-foreground dark:text-[#f7f8f8] mt-0.5" style={{ fontSize: '14px', fontWeight: 590, letterSpacing: '-0.13px' }}>{buyingSignals.budget_cycle.current_phase}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tech Stack Gaps */}
      {buyingSignals.tech_stack_gaps && buyingSignals.tech_stack_gaps.length > 0 && (
        <div className="border border-border/50 dark:border-white/[0.08] dark:bg-white/[0.02] rounded-lg overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3 border-b border-border/50 dark:border-white/[0.06] bg-muted/40 dark:bg-white/[0.03]">
            <Lightning className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="fill" />
            <span className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px', fontWeight: 590, letterSpacing: '-0.13px' }}>Tech Stack Gaps</span>
          </div>
          <div className="p-4 space-y-2">
            {buyingSignals.tech_stack_gaps.filter((x: any) => x && typeof x === 'object').map((gap: any, idx: number) => (
              <div key={idx} className="rounded-md border border-border/50 bg-card dark:border-white/[0.08] dark:bg-white/[0.02] p-3">
                <p className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}>{gap.gap}</p>
                {gap.current_tools?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {gap.current_tools.map((tool: string, tidx: number) => (
                      <span key={tidx} className="px-2 py-0.5 rounded-md border bg-muted/60 text-muted-foreground border-border/50 dark:bg-white/[0.04] dark:text-[#8a8f98] dark:border-white/[0.06]" style={{ fontSize: '10px', fontWeight: 510 }}>
                        {tool}
                      </span>
                    ))}
                  </div>
                )}
                {gap.opportunity && (
                  <div className="flex items-start gap-1.5 mt-2 text-[#5e6ad2] dark:text-[#a5b4fc]" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
                    <Lightning className="h-3 w-3 mt-0.5 flex-shrink-0" weight="fill" />
                    <span>{gap.opportunity}</span>
                  </div>
                )}
                <SourceCitation source={gap.source} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Competitive Intel */}
      {(competitiveIntel.current_tools?.length > 0 || competitiveIntel.competitor_weaknesses?.length > 0 || competitiveIntel.recent_competitor_moves?.length > 0) && (
        <div className="border border-border/50 dark:border-white/[0.08] dark:bg-white/[0.02] rounded-lg overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3 border-b border-border/50 dark:border-white/[0.06] bg-muted/40 dark:bg-white/[0.03]">
            <ShieldCheck className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="fill" />
            <span className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px', fontWeight: 590, letterSpacing: '-0.13px' }}>Competitive Landscape</span>
            {(() => {
              const totalItems = (competitiveIntel.current_tools?.length || 0) + (competitiveIntel.competitor_weaknesses?.length || 0) + (competitiveIntel.recent_competitor_moves?.length || 0);
              return totalItems > 0 ? (
                <span className="ml-auto bg-[#5e6ad2]/10 text-[#5e6ad2] border border-[#5e6ad2]/25 dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc] dark:border-[#5e6ad2]/30 px-2 py-0.5 rounded-full tabular-nums" style={{ fontSize: '10px', fontWeight: 590 }}>
                  {totalItems} insight{totalItems !== 1 ? 's' : ''}
                </span>
              ) : null;
            })()}
          </div>
          <div className="p-3 space-y-3">
            {/* Current Tools — grouped by category */}
            {competitiveIntel.current_tools && competitiveIntel.current_tools.length > 0 && (() => {
              const validTools = competitiveIntel.current_tools.filter((x: any) => x && typeof x === 'object');
              const byCategory: Record<string, any[]> = {};
              validTools.forEach((tool: any) => {
                const cat = tool.category || 'Other';
                if (!byCategory[cat]) byCategory[cat] = [];
                byCategory[cat].push(tool);
              });
              return (
                <div>
                  <div className="flex items-center gap-1.5 mb-2">
                    <Lightning className="h-3.5 w-3.5 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="fill" />
                    <span className="text-foreground dark:text-[#f7f8f8] uppercase tracking-[0.06em]" style={{ fontSize: '11px', fontWeight: 590 }}>Their Tech Stack</span>
                    <span className="text-muted-foreground dark:text-[#62666d] tabular-nums" style={{ fontSize: '10px' }}>({validTools.length})</span>
                  </div>
                  <div className="space-y-2">
                    {Object.entries(byCategory).map(([category, tools]) => (
                      <div key={category}>
                        <span className={EYEBROW} style={eyebrowStyle}>{category}</span>
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {tools.map((tool: any, idx: number) => (
                            <div key={idx} className="group relative">
                              <span className="inline-flex items-center px-2.5 py-1 rounded-md border bg-card text-foreground border-border/50 dark:bg-white/[0.04] dark:text-[#f7f8f8] dark:border-white/[0.06] hover:border-[#5e6ad2]/30 hover:bg-[#5e6ad2]/[0.06] dark:hover:border-[#5e6ad2]/30 dark:hover:bg-[#5e6ad2]/[0.10] transition-colors cursor-default" style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}>
                                {tool.tool}
                              </span>
                              {tool.evidence && (
                                <div className="hidden group-hover:block absolute bottom-full left-0 mb-1 z-20 w-max max-w-[220px]">
                                  <div className="bg-popover dark:bg-[#191a1b] border border-border/60 dark:border-white/[0.08] rounded-md px-2.5 py-1.5 shadow-lg text-muted-foreground dark:text-[#d0d6e0]" style={{ fontSize: '11px', letterSpacing: '-0.05px' }}>
                                    {tool.evidence}
                                    <SourceCitation source={tool.source} />
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Competitor Weaknesses — battle cards */}
            {competitiveIntel.competitor_weaknesses && competitiveIntel.competitor_weaknesses.length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <TargetPhosphor className="h-3.5 w-3.5 text-[#5e6ad2] dark:text-[#a5b4fc]" />
                  <span className="text-foreground dark:text-[#f7f8f8] uppercase tracking-[0.06em]" style={{ fontSize: '11px', fontWeight: 590 }}>Battle Cards</span>
                </div>
                <div className="space-y-2">
                  {competitiveIntel.competitor_weaknesses.filter((x: any) => x && typeof x === 'object').map((cw: any, idx: number) => (
                    <div key={idx} className="rounded-md border border-border/50 dark:border-white/[0.08] overflow-hidden">
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-muted/40 dark:bg-white/[0.03] border-b border-border/40 dark:border-white/[0.06]">
                        <ShieldCheck className="h-3 w-3 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="fill" />
                        <span className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '12px', fontWeight: 590, letterSpacing: '-0.1px' }}>{cw.competitor || 'Unknown'}</span>
                      </div>
                      <div className="grid grid-cols-2 divide-x divide-border/40 dark:divide-white/[0.06]">
                        <div className="px-3 py-2">
                          <div className="flex items-center gap-1 mb-1">
                            <XCirclePhosphor className="h-2.5 w-2.5 text-red-600 dark:text-red-400 flex-shrink-0" weight="fill" />
                            <span className="text-red-700 dark:text-red-300 uppercase tracking-[0.06em]" style={{ fontSize: '9px', fontWeight: 590 }}>Weakness</span>
                          </div>
                          <p className="text-foreground/80 dark:text-[#d0d6e0] leading-snug" style={{ fontSize: '11px', letterSpacing: '-0.05px' }}>{cw.weakness || '—'}</p>
                        </div>
                        <div className="px-3 py-2">
                          <div className="flex items-center gap-1 mb-1">
                            <CheckCircle className="h-2.5 w-2.5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" weight="fill" />
                            <span className="text-emerald-700 dark:text-emerald-300 uppercase tracking-[0.06em]" style={{ fontSize: '9px', fontWeight: 590 }}>Our Edge</span>
                          </div>
                          <p className="text-foreground/80 dark:text-[#d0d6e0] leading-snug" style={{ fontSize: '11px', letterSpacing: '-0.05px' }}>{cw.our_advantage || '—'}</p>
                        </div>
                      </div>
                      <SourceCitation source={cw.source} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Competitor Moves */}
            {competitiveIntel.recent_competitor_moves && competitiveIntel.recent_competitor_moves.length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <TrendUp className="h-3.5 w-3.5 text-[#5e6ad2] dark:text-[#a5b4fc]" />
                  <span className="text-foreground dark:text-[#f7f8f8] uppercase tracking-[0.06em]" style={{ fontSize: '11px', fontWeight: 590 }}>Recent Moves</span>
                </div>
                <div className="space-y-1.5">
                  {competitiveIntel.recent_competitor_moves.filter((x: any) => x && typeof x === 'object').map((cm: any, idx: number) => (
                    <div key={idx} className="rounded-md border border-border/50 dark:border-white/[0.06] bg-card dark:bg-white/[0.02] hover:bg-muted/20 dark:hover:bg-white/[0.03] transition-colors overflow-hidden">
                      <div className="flex items-stretch">
                        <div className="flex-1 px-3 py-2 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="px-1.5 py-0.5 rounded-md border bg-[#5e6ad2]/[0.10] text-[#5e6ad2] border-[#5e6ad2]/25 dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc] dark:border-[#5e6ad2]/30 flex-shrink-0" style={{ fontSize: '10px', fontWeight: 590 }}>
                              {cm.competitor || 'Unknown'}
                            </span>
                            {cm.date && <span className="text-muted-foreground dark:text-[#62666d] tabular-nums" style={{ fontSize: '10px' }}>{cm.date}</span>}
                          </div>
                          <p className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>{cm.move || ''}</p>
                          {cm.implication && (
                            <div className="mt-1.5 flex items-start gap-1.5 bg-amber-500/[0.05] dark:bg-amber-500/[0.08] border border-amber-500/20 dark:border-amber-500/25 rounded-md px-2 py-1.5">
                              <Warning className="h-3 w-3 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" weight="fill" />
                              <p className="text-amber-700 dark:text-amber-300" style={{ fontSize: '11px', letterSpacing: '-0.05px' }}>{cm.implication}</p>
                            </div>
                          )}
                          <SourceCitation source={cm.source} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Company Signals */}
      {enrichmentData?.company_signals?.signals?.length > 0 && (
        <div className="border border-border/50 dark:border-white/[0.08] dark:bg-white/[0.02] rounded-lg overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3 border-b border-border/50 dark:border-white/[0.06] bg-muted/40 dark:bg-white/[0.03]">
            <TrendUp className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc]" />
            <span className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px', fontWeight: 590, letterSpacing: '-0.13px' }}>Company Signals</span>
            <span className="ml-auto bg-muted/60 text-muted-foreground border border-border/50 dark:bg-white/[0.04] dark:text-[#8a8f98] dark:border-white/[0.06] px-2 py-0.5 rounded-full tabular-nums" style={{ fontSize: '10px', fontWeight: 590 }}>
              {enrichmentData.company_signals.signals.length}
            </span>
          </div>
          <div className="p-4 space-y-2">
            {enrichmentData.company_signals.signals.filter((s: any) => s && typeof s === 'object').map((signal: any, idx: number) => (
              <div key={idx} className="rounded-md border border-border/50 dark:border-white/[0.06] p-3 hover:bg-muted/20 dark:hover:bg-white/[0.03] transition-colors">
                <div className="flex items-start gap-3">
                  <span className={cn(
                    'px-2 py-0.5 rounded-md border shrink-0 mt-0.5 capitalize',
                    signal.type === 'funding' ? 'bg-emerald-500/10 border-emerald-500/25 text-emerald-700 dark:bg-emerald-500/[0.12] dark:border-emerald-500/30 dark:text-emerald-300' :
                    signal.type === 'leadership_change' ? 'bg-[#5e6ad2]/[0.10] border-[#5e6ad2]/25 text-[#5e6ad2] dark:bg-[#5e6ad2]/[0.14] dark:border-[#5e6ad2]/30 dark:text-[#a5b4fc]' :
                    signal.type === 'layoff' ? 'bg-red-500/10 border-red-500/25 text-red-700 dark:bg-red-500/[0.12] dark:border-red-500/30 dark:text-red-300' :
                    signal.type === 'acquisition' ? 'bg-[#5e6ad2]/[0.10] border-[#5e6ad2]/25 text-[#5e6ad2] dark:bg-[#5e6ad2]/[0.14] dark:border-[#5e6ad2]/30 dark:text-[#a5b4fc]' :
                    signal.type === 'expansion' ? 'bg-emerald-500/10 border-emerald-500/25 text-emerald-700 dark:bg-emerald-500/[0.12] dark:border-emerald-500/30 dark:text-emerald-300' :
                    'bg-amber-500/10 border-amber-500/25 text-amber-700 dark:bg-amber-500/[0.12] dark:border-amber-500/30 dark:text-amber-300'
                  )} style={{ fontSize: '10px', fontWeight: 590 }}>
                    {(signal.type || 'signal').replace(/_/g, ' ')}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}>{signal.headline}</p>
                    {signal.detail && <p className="text-muted-foreground dark:text-[#8a8f98] mt-0.5 leading-relaxed" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>{signal.detail}</p>}
                    {signal.impact && (
                      <p className="text-amber-700 dark:text-amber-300 mt-1 flex items-center gap-1" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
                        <Lightning className="h-3 w-3" weight="fill" /> {signal.impact}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-1.5">
                      {signal.date && <span className="text-muted-foreground dark:text-[#62666d] bg-muted/40 dark:bg-white/[0.04] px-1.5 py-0.5 rounded tabular-nums" style={{ fontSize: '10px' }}>{signal.date}</span>}
                      <SourceCitation source={signal.source} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {(enrichmentData.company_signals.headcount_trend || enrichmentData.company_signals.hiring_velocity) && (
              <div className="mt-2 pt-3 border-t border-border/30 dark:border-white/[0.06] flex gap-3">
                {enrichmentData.company_signals.headcount_trend && (
                  <div className="flex-1 bg-muted/40 dark:bg-white/[0.03] rounded-md p-2.5 border border-border/50 dark:border-white/[0.06]">
                    <p className={EYEBROW} style={eyebrowStyle}>Headcount</p>
                    <p className="text-foreground dark:text-[#f7f8f8] mt-0.5" style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}>{enrichmentData.company_signals.headcount_trend}</p>
                  </div>
                )}
                {enrichmentData.company_signals.hiring_velocity && (
                  <div className="flex-1 bg-muted/40 dark:bg-white/[0.03] rounded-md p-2.5 border border-border/50 dark:border-white/[0.06]">
                    <p className={EYEBROW} style={eyebrowStyle}>Hiring</p>
                    <p className="text-foreground dark:text-[#f7f8f8] mt-0.5" style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}>{enrichmentData.company_signals.hiring_velocity}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ResearchTab
// ---------------------------------------------------------------------------

function ResearchTab({ enrichmentData, enrichmentResults }: TabProps) {
  const [expandedCustomAi, setExpandedCustomAi] = useState<Record<string, boolean>>({});

  if (!enrichmentData && (!enrichmentResults || Object.keys(enrichmentResults).length === 0)) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="h-12 w-12 rounded-md bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center mb-4">
          <BookOpenPhosphor className="h-5 w-5 text-muted-foreground dark:text-[#8a8f98]" />
        </div>
        <p className="text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '13px', letterSpacing: '-0.13px' }}>Enrich this contact to see research data</p>
      </div>
    );
  }

  const person = enrichmentData?.person || {};
  const company = enrichmentData?.company || {};
  const sector = enrichmentData?.sector || {};
  const economy = enrichmentData?.economy || {};
  const contentRelevance = enrichmentData?.content_relevance || {};
  const relationshipMap = enrichmentData?.relationship_map || {};
  const metadata = enrichmentData?.metadata || {};

  const rawCustomAiData = enrichmentResults?.native?.data?.custom_ai;
  const customAiEntries: [string, any][] = rawCustomAiData
    ? (rawCustomAiData.recipe_name !== undefined || rawCustomAiData.output_mode !== undefined
        ? [['default', rawCustomAiData]]
        : Object.entries(rawCustomAiData))
    : [];

  const results = enrichmentResults || {};

  // Merge skills from all sources (native AI + PDL + Explorium), deduplicated
  // Values can be plain strings OR {value, sources} objects from the merge service
  const pdlData = results.peopledatalabs?.status === 'completed' ? results.peopledatalabs.data : null;
  const expData = results.explorium?.status === 'completed' ? results.explorium.data : null;
  const _toArr = (v: any): any[] => Array.isArray(v) ? v : [];
  const _extractStr = (s: any): string => typeof s === 'string' ? s : (s?.value || String(s));
  const allSkills = Array.from(new Set([
    ..._toArr(person.skills),
    ..._toArr(pdlData?.person?.skills),
    ..._toArr(expData?.person?.skills),
  ].filter(Boolean).map(_extractStr)));
  const allInterests = Array.from(new Set([
    ..._toArr(person.interests),
    ..._toArr(pdlData?.person?.interests),
    ..._toArr(expData?.person?.interests),
  ].filter(Boolean).map(_extractStr)));
  const allEducation = Array.isArray(person.education) && person.education.length > 0
    ? person.education
    : (_toArr(pdlData?.person?.education).length > 0 ? pdlData.person.education : _toArr(expData?.person?.education));
  const socialProfiles = {
    linkedin: pdlData?.person?.linkedin_url || expData?.person?.linkedin_url,
    twitter: pdlData?.person?.twitter_url,
    github: pdlData?.person?.github_url,
    facebook: pdlData?.person?.facebook_url,
  };

  // PDL and Explorium data is now merged into the unified sections above.
  // Only show Financials and News as separate panels (unique data types).
  const providerPanels = [
    { id: 'financials', label: 'Financials', hasData: !!(results.fmp?.status === 'completed' || results.alpha_vantage?.status === 'completed' || results.sec_edgar?.status === 'completed'), panel: <FinancialResultsPanel results={results} onEnrich={() => {}} /> },
    { id: 'news', label: 'News', hasData: results.news?.status === 'completed', panel: <NewsResultsPanel results={results.news} onEnrich={() => {}} /> },
  ];
  const activeProviderPanels = providerPanels.filter(p => p.hasData);

  const EYEBROW = 'text-muted-foreground dark:text-[#8a8f98] uppercase tracking-[0.06em]';
  const eyebrowStyle = { fontSize: '10px', fontWeight: 590 } as const;
  const TILE = 'rounded-lg p-3 border border-border/50 bg-muted/40 dark:bg-white/[0.02] dark:border-white/[0.08]';
  const SECTION_CARD = 'border border-border/50 dark:border-white/[0.08] dark:bg-white/[0.02] rounded-lg overflow-hidden';
  const SECTION_HEADER = 'flex items-center gap-2 px-5 py-3 border-b border-border/50 dark:border-white/[0.06] bg-muted/40 dark:bg-white/[0.03]';

  return (
    <div className="space-y-6">
      {/* Person — Hero Profile Card */}
      {enrichmentData && (
        <div className={SECTION_CARD}>
          <div className={cn('bg-[#5e6ad2]/[0.06] dark:bg-[#5e6ad2]/[0.10] border-b border-[#5e6ad2]/15 dark:border-[#5e6ad2]/20 px-5', person.bio ? 'py-4' : 'py-3')}>
            <div className={cn('flex items-center gap-2', person.bio && 'mb-3')}>
              <User className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc]" />
              <span className="text-foreground dark:text-[#f7f8f8]" style={sectionTitleStyle}>Person</span>
            </div>
            {person.bio && (
              <p className="text-foreground/90 dark:text-[#d0d6e0] leading-relaxed" style={{ fontSize: '13px', letterSpacing: '-0.13px' }}>
                {person.bio}
                {person.bio_source && <SourceCitation source={person.bio_source} />}
              </p>
            )}
          </div>
          <div className="p-5 space-y-4">
            {/* Education — styled cards */}
            {allEducation.length > 0 && (
              <div>
                <span className={EYEBROW} style={eyebrowStyle}>Education</span>
                <div className="mt-2 space-y-2">
                  {allEducation.filter((e: any) => e).map((e: any, idx: number) => {
                    const school = typeof e === 'string' ? e : (e.school_name || e.school || '');
                    const degree = typeof e === 'string' ? '' : (e.degree || '');
                    const major = typeof e === 'string' ? '' : (e.major || e.field_of_study || '');
                    return (
                      <div key={idx} className="flex items-start gap-3 bg-muted/40 dark:bg-white/[0.02] rounded-md p-3 border border-border/50 dark:border-white/[0.06]">
                        <div className="h-8 w-8 rounded-md bg-[#5e6ad2]/[0.10] dark:bg-[#5e6ad2]/[0.14] border border-[#5e6ad2]/20 dark:border-[#5e6ad2]/25 flex items-center justify-center flex-shrink-0">
                          <BookOpenPhosphor className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc]" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}>{school}</p>
                          {(degree || major) && (
                            <p className="text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>{[degree, major].filter(Boolean).join(' in ')}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Achievements */}
            {person.notable_achievements && person.notable_achievements.length > 0 && (
              <div>
                <span className={EYEBROW} style={eyebrowStyle}>Achievements</span>
                <div className="mt-2 space-y-1.5">
                  {person.notable_achievements.filter((a: any) => a).map((achievement: string, idx: number) => (
                    <div key={idx} className="flex items-start gap-2">
                      <Sparkle className="h-3.5 w-3.5 text-[#5e6ad2] dark:text-[#a5b4fc] mt-0.5 flex-shrink-0" weight="fill" />
                      <span className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px', letterSpacing: '-0.13px' }}>{achievement}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Skills */}
            {allSkills.length > 0 && (
              <div>
                <span className={EYEBROW} style={eyebrowStyle}>Skills</span>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {allSkills.map((skill: any, idx: number) => (
                    <span key={idx} className="px-2.5 py-1 rounded-md border bg-[#5e6ad2]/[0.10] text-[#5e6ad2] border-[#5e6ad2]/25 dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc] dark:border-[#5e6ad2]/30" style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}>
                      {typeof skill === 'string' ? skill : skill?.value || String(skill)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Interests */}
            {allInterests.length > 0 && (
              <div>
                <span className={EYEBROW} style={eyebrowStyle}>Interests</span>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {allInterests.map((interest: any, idx: number) => (
                    <span key={idx} className="px-2.5 py-1 rounded-md border bg-muted/60 text-muted-foreground border-border/50 dark:bg-white/[0.04] dark:text-[#d0d6e0] dark:border-white/[0.06]" style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}>
                      {typeof interest === 'string' ? interest : interest?.value || String(interest)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Social Profiles */}
            {(Object.values(socialProfiles).some(Boolean)) && (
              <div className="flex flex-wrap gap-2 pt-1">
                {socialProfiles.linkedin && (
                  <a href={socialProfiles.linkedin} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border/60 hover:bg-muted/50 hover:border-border/80 dark:border-white/[0.08] dark:hover:bg-white/[0.04] dark:hover:border-white/[0.14] text-foreground dark:text-[#e2e4e7] transition-colors" style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}>
                    <LinkedinLogo className="h-3.5 w-3.5 text-[#0A66C2] dark:text-[#5e9ad8]" weight="fill" /> LinkedIn
                  </a>
                )}
                {socialProfiles.twitter && (
                  <a href={socialProfiles.twitter} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border/60 hover:bg-muted/50 hover:border-border/80 dark:border-white/[0.08] dark:hover:bg-white/[0.04] dark:hover:border-white/[0.14] text-foreground dark:text-[#e2e4e7] transition-colors" style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}>
                    <TwitterLogo className="h-3.5 w-3.5 text-[#1DA1F2] dark:text-[#4bb3f5]" weight="fill" /> Twitter
                  </a>
                )}
                {socialProfiles.github && (
                  <a href={socialProfiles.github} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border/60 hover:bg-muted/50 hover:border-border/80 dark:border-white/[0.08] dark:hover:bg-white/[0.04] dark:hover:border-white/[0.14] text-foreground dark:text-[#e2e4e7] transition-colors" style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}>
                    <Globe className="h-3.5 w-3.5" /> GitHub
                  </a>
                )}
              </div>
            )}

            {(!person.bio && allSkills.length === 0 && allEducation.length === 0) && (
              <p className="text-muted-foreground dark:text-[#8a8f98] italic" style={{ fontSize: '13px', letterSpacing: '-0.13px' }}>No person data available</p>
            )}
          </div>
        </div>
      )}

      {/* Career Timeline — Visual timeline with dots */}
      {(enrichmentData?.person?.career_history?.length > 0 || enrichmentData?.person?.previous_roles?.length > 0) && (
        <div className={SECTION_CARD}>
          <div className={SECTION_HEADER}>
            <BriefcasePhosphor className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc]" />
            <span className="text-foreground dark:text-[#f7f8f8]" style={sectionTitleStyle}>Career Timeline</span>
          </div>
          <div className="p-5">
            {enrichmentData.person.career_history?.length > 0 ? (
              <div className="relative">
                <div className="absolute left-[11px] top-3 bottom-3 w-px bg-gradient-to-b from-[#5e6ad2]/40 via-[#5e6ad2]/20 to-transparent dark:from-[#a5b4fc]/40 dark:via-[#a5b4fc]/20" />
                <div className="space-y-4">
                  {enrichmentData.person.career_history.filter((e: any) => e && typeof e === 'object').map((entry: any, idx: number) => (
                    <div key={idx} className="flex items-start gap-4 relative">
                      <div className={cn(
                        'flex-shrink-0 h-[22px] w-[22px] rounded-full border-2 flex items-center justify-center z-10',
                        idx === 0
                          ? 'border-[#5e6ad2] bg-[#5e6ad2]/[0.12] dark:border-[#a5b4fc] dark:bg-[#5e6ad2]/[0.20]'
                          : 'border-border dark:border-white/[0.10] bg-card dark:bg-[#0f1011]'
                      )}>
                        <div className={cn(
                          'h-2 w-2 rounded-full',
                          idx === 0 ? 'bg-[#5e6ad2] dark:bg-[#a5b4fc]' : 'bg-muted-foreground/30 dark:bg-white/[0.20]'
                        )} />
                      </div>
                      <div className="flex-1 min-w-0 -mt-0.5">
                        <p className={cn(
                          idx === 0 ? 'text-foreground dark:text-[#f7f8f8]' : 'text-foreground/80 dark:text-[#d0d6e0]'
                        )} style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}>
                          {entry.title}
                        </p>
                        <p className="text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}>{entry.company}</p>
                        <div className="flex items-center gap-2 mt-1">
                          {(entry.start_date || entry.end_date) && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-muted/60 text-muted-foreground border border-border/50 dark:bg-white/[0.04] dark:text-[#8a8f98] dark:border-white/[0.06] tabular-nums" style={{ fontSize: '10px', fontWeight: 510 }}>
                              <ClockPhosphor className="h-2.5 w-2.5" />
                              {entry.start_date || '?'} — {entry.end_date || 'Present'}
                            </span>
                          )}
                          {entry.duration && (
                            <span className="text-muted-foreground dark:text-[#62666d]" style={{ fontSize: '10px' }}>({entry.duration})</span>
                          )}
                        </div>
                        <SourceCitation source={entry.source} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {enrichmentData.person.previous_roles.filter((r: any) => r).map((role: string, idx: number) => (
                  <div key={idx} className="flex items-center gap-2 text-foreground dark:text-[#f7f8f8] bg-muted/40 dark:bg-white/[0.02] border border-border/50 dark:border-white/[0.06] rounded-md px-3 py-2" style={{ fontSize: '13px', letterSpacing: '-0.13px' }}>
                    <BriefcasePhosphor className="h-3.5 w-3.5 text-muted-foreground dark:text-[#8a8f98] flex-shrink-0" />
                    {role}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Company — Metric cards + detail sections */}
      {enrichmentData && (
        <div className={SECTION_CARD}>
          <div className={SECTION_HEADER}>
            <Buildings className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc]" />
            <span className="text-foreground dark:text-[#f7f8f8]" style={sectionTitleStyle}>Company</span>
          </div>
          <div className="p-5 space-y-4">
            {/* Description */}
            {company.description && (
              <p className="text-foreground/90 dark:text-[#d0d6e0] leading-relaxed bg-muted/40 dark:bg-white/[0.02] rounded-md p-3 border border-border/50 dark:border-white/[0.06]" style={{ fontSize: '13px', letterSpacing: '-0.13px' }}>
                {company.description}
              </p>
            )}

            {/* Key Metrics Grid */}
            {(company.founded_year || company.employee_count || company.headquarters || company.funding_stage || company.total_funding || company.revenue_estimate) && (
              <div className="grid grid-cols-3 gap-2">
                {company.founded_year && (
                  <div className={TILE}>
                    <p className={EYEBROW} style={eyebrowStyle}>Founded</p>
                    <p className="text-foreground dark:text-[#f7f8f8] mt-0.5 tabular-nums" style={{ fontSize: '14px', fontWeight: 590, letterSpacing: '-0.13px' }}>{company.founded_year}</p>
                  </div>
                )}
                {company.employee_count && (
                  <div className={TILE}>
                    <p className={EYEBROW} style={eyebrowStyle}>Employees</p>
                    <p className="text-foreground dark:text-[#f7f8f8] mt-0.5 tabular-nums" style={{ fontSize: '14px', fontWeight: 590, letterSpacing: '-0.13px' }}>{company.employee_count}</p>
                  </div>
                )}
                {company.headquarters && (
                  <div className={TILE}>
                    <p className={EYEBROW} style={eyebrowStyle}>HQ</p>
                    <p className="text-foreground dark:text-[#f7f8f8] mt-0.5" style={{ fontSize: '14px', fontWeight: 590, letterSpacing: '-0.13px' }}>{company.headquarters}</p>
                  </div>
                )}
                {company.funding_stage && (
                  <div className={TILE}>
                    <p className={EYEBROW} style={eyebrowStyle}>Funding</p>
                    <p className="text-foreground dark:text-[#f7f8f8] mt-0.5" style={{ fontSize: '14px', fontWeight: 590, letterSpacing: '-0.13px' }}>{company.funding_stage}</p>
                  </div>
                )}
                {company.total_funding && (
                  <div className={TILE}>
                    <p className={EYEBROW} style={eyebrowStyle}>Total Raised</p>
                    <p className="text-foreground dark:text-[#f7f8f8] mt-0.5" style={{ fontSize: '14px', fontWeight: 590, letterSpacing: '-0.13px' }}>{company.total_funding}</p>
                  </div>
                )}
                {company.revenue_estimate && (
                  <div className={TILE}>
                    <p className={EYEBROW} style={eyebrowStyle}>Revenue</p>
                    <p className="text-foreground dark:text-[#f7f8f8] mt-0.5" style={{ fontSize: '14px', fontWeight: 590, letterSpacing: '-0.13px' }}>{company.revenue_estimate}</p>
                  </div>
                )}
              </div>
            )}

            {/* Growth indicators */}
            {(company.headcount_trend || company.hiring_velocity) && (
              <div className="flex gap-3">
                {company.headcount_trend && (
                  <div className="flex-1 rounded-md p-3 bg-muted/40 border border-border/50 dark:bg-white/[0.02] dark:border-white/[0.08]">
                    <div className="flex items-center gap-1.5 mb-1">
                      <TrendUp className="h-3.5 w-3.5 text-[#5e6ad2] dark:text-[#a5b4fc]" />
                      <span className={EYEBROW} style={eyebrowStyle}>Headcount Trend</span>
                    </div>
                    <p className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}>{company.headcount_trend}</p>
                  </div>
                )}
                {company.hiring_velocity && (
                  <div className="flex-1 rounded-md p-3 bg-muted/40 border border-border/50 dark:bg-white/[0.02] dark:border-white/[0.08]">
                    <div className="flex items-center gap-1.5 mb-1">
                      <Lightning className="h-3.5 w-3.5 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="fill" />
                      <span className={EYEBROW} style={eyebrowStyle}>Hiring Velocity</span>
                    </div>
                    <p className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}>{company.hiring_velocity}</p>
                  </div>
                )}
              </div>
            )}

            {/* Products & Services */}
            {company.products_services && company.products_services.length > 0 && (
              <div>
                <span className={EYEBROW} style={eyebrowStyle}>Products & Services</span>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {company.products_services.filter((p: any) => p).map((product: any, idx: number) => (
                    <span key={idx} className="px-2.5 py-1 rounded-md border bg-muted/60 text-foreground border-border/50 dark:bg-white/[0.04] dark:text-[#f7f8f8] dark:border-white/[0.06]" style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}>
                      {product}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Tech Stack */}
            {company.tech_stack && company.tech_stack.length > 0 && (
              <div>
                <span className={EYEBROW} style={eyebrowStyle}>Tech Stack</span>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {company.tech_stack.filter((t: any) => t).map((tech: any, idx: number) => (
                    <span key={idx} className="px-2.5 py-1 rounded-md border bg-muted/60 text-foreground border-border/50 dark:bg-white/[0.04] dark:text-[#f7f8f8] dark:border-white/[0.06]" style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}>
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Key People */}
            {company.key_people && company.key_people.length > 0 && (
              <div>
                <span className={EYEBROW} style={eyebrowStyle}>Key People</span>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {company.key_people.filter((p: any) => p?.name).map((kp: any, idx: number) => (
                    <div key={idx} className="flex items-center gap-2.5 bg-muted/40 dark:bg-white/[0.02] rounded-md p-2.5 border border-border/50 dark:border-white/[0.06]">
                      <div className="h-7 w-7 rounded-full bg-[#5e6ad2]/[0.10] dark:bg-[#5e6ad2]/[0.14] border border-[#5e6ad2]/20 dark:border-[#5e6ad2]/25 flex items-center justify-center flex-shrink-0">
                        <User className="h-3.5 w-3.5 text-[#5e6ad2] dark:text-[#a5b4fc]" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-foreground dark:text-[#f7f8f8] truncate" style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}>{kp.name}</p>
                        {kp.role && <p className="text-muted-foreground dark:text-[#8a8f98] truncate" style={{ fontSize: '10px' }}>{kp.role}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(!company.description && !company.founded_year && !company.employee_count) && (
              <p className="text-muted-foreground dark:text-[#8a8f98] italic" style={{ fontSize: '13px', letterSpacing: '-0.13px' }}>No company data available</p>
            )}
          </div>
        </div>
      )}

      {/* Company Signals — Card-based */}
      {enrichmentData?.company_signals?.signals?.length > 0 && (
        <div className={SECTION_CARD}>
          <div className={SECTION_HEADER}>
            <TrendUp className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc]" />
            <span className="text-foreground dark:text-[#f7f8f8]" style={sectionTitleStyle}>Company Signals</span>
            <span className="ml-auto bg-muted/60 text-muted-foreground border border-border/50 dark:bg-white/[0.04] dark:text-[#8a8f98] dark:border-white/[0.06] px-2 py-0.5 rounded-full tabular-nums" style={{ fontSize: '10px', fontWeight: 590 }}>
              {enrichmentData.company_signals.signals.length} signal{enrichmentData.company_signals.signals.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="p-4 space-y-2">
            {enrichmentData.company_signals.signals.filter((s: any) => s && typeof s === 'object').map((signal: any, idx: number) => (
              <div key={idx} className="rounded-md border border-border/50 dark:border-white/[0.06] p-3 hover:bg-muted/20 dark:hover:bg-white/[0.03] transition-colors">
                <div className="flex items-start gap-3">
                  <span className={cn(
                    'px-2 py-0.5 rounded-md border shrink-0 mt-0.5 capitalize',
                    signal.type === 'funding' ? 'bg-emerald-500/10 border-emerald-500/25 text-emerald-700 dark:bg-emerald-500/[0.12] dark:border-emerald-500/30 dark:text-emerald-300' :
                    signal.type === 'leadership_change' ? 'bg-[#5e6ad2]/[0.10] border-[#5e6ad2]/25 text-[#5e6ad2] dark:bg-[#5e6ad2]/[0.14] dark:border-[#5e6ad2]/30 dark:text-[#a5b4fc]' :
                    signal.type === 'layoff' ? 'bg-red-500/10 border-red-500/25 text-red-700 dark:bg-red-500/[0.12] dark:border-red-500/30 dark:text-red-300' :
                    signal.type === 'acquisition' ? 'bg-[#5e6ad2]/[0.10] border-[#5e6ad2]/25 text-[#5e6ad2] dark:bg-[#5e6ad2]/[0.14] dark:border-[#5e6ad2]/30 dark:text-[#a5b4fc]' :
                    signal.type === 'expansion' ? 'bg-emerald-500/10 border-emerald-500/25 text-emerald-700 dark:bg-emerald-500/[0.12] dark:border-emerald-500/30 dark:text-emerald-300' :
                    'bg-amber-500/10 border-amber-500/25 text-amber-700 dark:bg-amber-500/[0.12] dark:border-amber-500/30 dark:text-amber-300'
                  )} style={{ fontSize: '10px', fontWeight: 590 }}>
                    {(signal.type || 'signal').replace(/_/g, ' ')}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}>{signal.headline}</p>
                    {signal.detail && <p className="text-muted-foreground dark:text-[#8a8f98] mt-0.5 leading-relaxed" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>{signal.detail}</p>}
                    {signal.impact && (
                      <p className="text-amber-700 dark:text-amber-300 mt-1 flex items-center gap-1" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
                        <Lightning className="h-3 w-3" weight="fill" /> {signal.impact}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-1.5">
                      {signal.date && <span className="text-muted-foreground dark:text-[#62666d] bg-muted/40 dark:bg-white/[0.04] px-1.5 py-0.5 rounded tabular-nums" style={{ fontSize: '10px' }}>{signal.date}</span>}
                      <SourceCitation source={signal.source} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {enrichmentData.company_signals.headcount_trend && (
              <div className="mt-2 pt-3 border-t border-border/30 dark:border-white/[0.06] flex gap-3">
                <div className="flex-1 bg-muted/40 dark:bg-white/[0.03] rounded-md p-2.5 border border-border/50 dark:border-white/[0.06]">
                  <p className={EYEBROW} style={eyebrowStyle}>Headcount</p>
                  <p className="text-foreground dark:text-[#f7f8f8] mt-0.5" style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}>{enrichmentData.company_signals.headcount_trend}</p>
                </div>
                {enrichmentData.company_signals.hiring_velocity && (
                  <div className="flex-1 bg-muted/40 dark:bg-white/[0.03] rounded-md p-2.5 border border-border/50 dark:border-white/[0.06]">
                    <p className={EYEBROW} style={eyebrowStyle}>Hiring</p>
                    <p className="text-foreground dark:text-[#f7f8f8] mt-0.5" style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}>{enrichmentData.company_signals.hiring_velocity}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sector & Industry — Visual grid */}
      {enrichmentData && (sector.industry || sector.market_size) && (
        <div className={SECTION_CARD}>
          <div className={SECTION_HEADER}>
            <ChartBar className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc]" />
            <span className="text-foreground dark:text-[#f7f8f8]" style={sectionTitleStyle}>Sector & Industry</span>
          </div>
          <div className="p-5 space-y-4">
            {/* Key metrics grid */}
            <div className="grid grid-cols-2 gap-2">
              {sector.industry && (
                <div className={TILE}>
                  <p className={EYEBROW} style={eyebrowStyle}>Industry</p>
                  <p className="text-foreground dark:text-[#f7f8f8] mt-0.5" style={{ fontSize: '14px', fontWeight: 590, letterSpacing: '-0.13px' }}>{sector.industry}</p>
                </div>
              )}
              {sector.sub_sector && (
                <div className={TILE}>
                  <p className={EYEBROW} style={eyebrowStyle}>Sub-sector</p>
                  <p className="text-foreground dark:text-[#f7f8f8] mt-0.5" style={{ fontSize: '14px', fontWeight: 590, letterSpacing: '-0.13px' }}>{sector.sub_sector}</p>
                </div>
              )}
              {sector.market_size && (
                <div className={TILE}>
                  <p className={EYEBROW} style={eyebrowStyle}>Market Size</p>
                  <p className="text-foreground dark:text-[#f7f8f8] mt-0.5" style={{ fontSize: '14px', fontWeight: 590, letterSpacing: '-0.13px' }}>{sector.market_size}</p>
                </div>
              )}
              {sector.growth_rate && (
                <div className={TILE}>
                  <p className={EYEBROW} style={eyebrowStyle}>Growth Rate</p>
                  <p className="text-foreground dark:text-[#f7f8f8] mt-0.5" style={{ fontSize: '14px', fontWeight: 590, letterSpacing: '-0.13px' }}>{sector.growth_rate}</p>
                </div>
              )}
            </div>

            {/* Key Trends */}
            {sector.key_trends && sector.key_trends.length > 0 && (
              <div>
                <span className={EYEBROW} style={eyebrowStyle}>Key Trends</span>
                <div className="mt-2 space-y-1.5">
                  {sector.key_trends.filter((t: any) => t).map((trend: string, idx: number) => (
                    <div key={idx} className="flex items-start gap-2">
                      <TrendUp className="h-3.5 w-3.5 text-[#5e6ad2] dark:text-[#a5b4fc] mt-0.5 flex-shrink-0" />
                      <span className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px', letterSpacing: '-0.13px' }}>{trend}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Competitors */}
            {sector.top_competitors && sector.top_competitors.length > 0 && (
              <div>
                <span className={EYEBROW} style={eyebrowStyle}>Top Competitors</span>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {sector.top_competitors.filter((c: any) => c).map((comp: string, idx: number) => (
                    <span key={idx} className="px-2.5 py-1 rounded-md border bg-muted/60 text-foreground border-border/50 dark:bg-white/[0.04] dark:text-[#f7f8f8] dark:border-white/[0.06]" style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}>
                      {comp}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {sector.regulatory_environment && (
              <div className="bg-muted/40 border border-border/50 dark:bg-white/[0.02] dark:border-white/[0.08] rounded-md p-3">
                <div className="flex items-center gap-1.5 mb-1">
                  <ShieldCheck className="h-3.5 w-3.5 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="fill" />
                  <span className={EYEBROW} style={eyebrowStyle}>Regulatory</span>
                </div>
                <p className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px', letterSpacing: '-0.13px' }}>{sector.regulatory_environment}</p>
              </div>
            )}

            {sector.innovation_areas && sector.innovation_areas.length > 0 && (
              <div>
                <span className={EYEBROW} style={eyebrowStyle}>Innovation Areas</span>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {sector.innovation_areas.filter((a: any) => a).map((area: string, idx: number) => (
                    <span key={idx} className="px-2.5 py-1 rounded-md border bg-muted/60 text-foreground border-border/50 dark:bg-white/[0.04] dark:text-[#f7f8f8] dark:border-white/[0.06]" style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}>
                      {area}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Economy & Market */}
      {enrichmentData && (economy.region || economy.economic_conditions) && (
        <div className={SECTION_CARD}>
          <div className={SECTION_HEADER}>
            <Globe className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc]" />
            <span className="text-foreground dark:text-[#f7f8f8]" style={sectionTitleStyle}>Economy & Market</span>
          </div>
          <div className="p-5">
            <div className="grid grid-cols-2 gap-3">
              {economy.region && (
                <div className={TILE}>
                  <p className={EYEBROW} style={eyebrowStyle}>Region</p>
                  <p className="text-foreground dark:text-[#f7f8f8] mt-0.5" style={{ fontSize: '14px', fontWeight: 590, letterSpacing: '-0.13px' }}>{economy.region}</p>
                </div>
              )}
              {economy.gdp_growth && (
                <div className={TILE}>
                  <p className={EYEBROW} style={eyebrowStyle}>GDP Growth</p>
                  <p className="text-foreground dark:text-[#f7f8f8] mt-0.5 tabular-nums" style={{ fontSize: '14px', fontWeight: 590, letterSpacing: '-0.13px' }}>{economy.gdp_growth}</p>
                </div>
              )}
              {economy.business_climate && (
                <div className={TILE}>
                  <p className={EYEBROW} style={eyebrowStyle}>Business Climate</p>
                  <p className="text-foreground dark:text-[#f7f8f8] mt-0.5" style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}>{economy.business_climate}</p>
                </div>
              )}
              {economy.market_outlook && (
                <div className={TILE}>
                  <p className={EYEBROW} style={eyebrowStyle}>Market Outlook</p>
                  <p className="text-foreground dark:text-[#f7f8f8] mt-0.5" style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}>{economy.market_outlook}</p>
                </div>
              )}
            </div>
            {economy.economic_conditions && (
              <p className="text-foreground/90 dark:text-[#d0d6e0] leading-relaxed mt-3 bg-muted/40 dark:bg-white/[0.02] rounded-md p-3 border border-border/50 dark:border-white/[0.06]" style={{ fontSize: '13px', letterSpacing: '-0.13px' }}>
                {economy.economic_conditions}
              </p>
            )}
            {economy.investment_climate && (
              <div className="mt-3 bg-muted/40 dark:bg-white/[0.02] rounded-md p-3 border border-border/50 dark:border-white/[0.06]">
                <p className={cn(EYEBROW, 'mb-1')} style={eyebrowStyle}>Investment Climate</p>
                <p className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px', letterSpacing: '-0.13px' }}>{economy.investment_climate}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Content Relevance */}
      {enrichmentData && (contentRelevance.engaged_topics?.length > 0 || contentRelevance.speaking_and_content?.length > 0) && (
        <div className={SECTION_CARD}>
          <div className={SECTION_HEADER}>
            <BookOpenPhosphor className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc]" />
            <span className="text-foreground dark:text-[#f7f8f8]" style={sectionTitleStyle}>Content Relevance</span>
          </div>
          <div className="p-5 space-y-4">
            {contentRelevance.engaged_topics && contentRelevance.engaged_topics.length > 0 && (
              <div>
                <span className={EYEBROW} style={eyebrowStyle}>Engaged Topics</span>
                <div className="mt-2 space-y-2">
                  {contentRelevance.engaged_topics.filter((x: any) => x && typeof x === 'object').map((et: any, idx: number) => (
                    <div key={idx} className="flex items-center justify-between bg-muted/40 dark:bg-white/[0.02] rounded-md p-2.5 border border-border/50 dark:border-white/[0.06]">
                      <div className="flex items-center gap-2">
                        <BookOpenPhosphor className="h-3.5 w-3.5 text-muted-foreground dark:text-[#8a8f98] flex-shrink-0" />
                        <span className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}>{et.topic}</span>
                        {et.engagement_type && (
                          <span className="text-muted-foreground dark:text-[#62666d] px-1.5 py-0.5 bg-muted/60 dark:bg-white/[0.04] rounded" style={{ fontSize: '10px', fontWeight: 510 }}>
                            {et.engagement_type}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5">
                        {et.recency && (
                          <span className="px-2 py-0.5 rounded-md border bg-[#5e6ad2]/[0.10] text-[#5e6ad2] border-[#5e6ad2]/25 dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc] dark:border-[#5e6ad2]/30" style={{ fontSize: '10px', fontWeight: 510 }}>
                            {et.recency}
                          </span>
                        )}
                        <SourceCitation source={et.source} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {contentRelevance.speaking_and_content && contentRelevance.speaking_and_content.length > 0 && (
              <div>
                <span className={EYEBROW} style={eyebrowStyle}>Speaking & Content</span>
                <div className="mt-2 space-y-2">
                  {contentRelevance.speaking_and_content.filter((sc: any) => sc && typeof sc === 'object').map((sc: any, idx: number) => (
                    <div key={idx} className="rounded-md border border-[#5e6ad2]/20 bg-[#5e6ad2]/[0.05] dark:border-[#5e6ad2]/25 dark:bg-[#5e6ad2]/[0.08] p-3">
                      <p className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}>{sc.title || 'Untitled'}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {sc.type && (
                          <span className="px-2 py-0.5 rounded-md border bg-[#5e6ad2]/[0.10] text-[#5e6ad2] border-[#5e6ad2]/25 dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc] dark:border-[#5e6ad2]/30 capitalize" style={{ fontSize: '10px', fontWeight: 510 }}>
                            {sc.type.replace(/_/g, ' ')}
                          </span>
                        )}
                        {sc.venue && <span className="text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>at {sc.venue}</span>}
                        {sc.date && <span className="text-muted-foreground dark:text-[#62666d] tabular-nums" style={{ fontSize: '12px' }}>({sc.date})</span>}
                      </div>
                      <SourceCitation source={sc.source} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Relationship Map */}
      {enrichmentData && (relationshipMap.other_contacts_at_company?.length > 0 || relationshipMap.reporting_structure || relationshipMap.champion_score) && (
        <div className={SECTION_CARD}>
          <div className={SECTION_HEADER}>
            <UsersThree className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc]" />
            <span className="text-foreground dark:text-[#f7f8f8]" style={sectionTitleStyle}>Relationship Map</span>
          </div>
          <div className="p-5 space-y-4">
            {/* Champion Score — prominent */}
            {relationshipMap.champion_score && (
              <div className="flex items-center gap-4 bg-[#5e6ad2]/[0.06] dark:bg-[#5e6ad2]/[0.10] rounded-md p-4 border border-[#5e6ad2]/20 dark:border-[#5e6ad2]/25">
                <div className={cn(
                  'h-14 w-14 rounded-md flex items-center justify-center tabular-nums border',
                  (relationshipMap.champion_score.score || 0) >= 7 ? 'bg-emerald-500/10 text-emerald-700 border-emerald-500/25 dark:bg-emerald-500/[0.14] dark:text-emerald-300 dark:border-emerald-500/30' :
                  'bg-[#5e6ad2]/10 text-[#5e6ad2] border-[#5e6ad2]/25 dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc] dark:border-[#5e6ad2]/30'
                )} style={{ fontSize: '22px', fontWeight: 590, letterSpacing: '-0.24px' }}>
                  {relationshipMap.champion_score.score}
                </div>
                <div>
                  <p className={EYEBROW} style={eyebrowStyle}>Champion Score</p>
                  {relationshipMap.champion_score.reasoning && (
                    <p className="text-foreground dark:text-[#f7f8f8] mt-0.5" style={{ fontSize: '13px', letterSpacing: '-0.13px' }}>{relationshipMap.champion_score.reasoning}</p>
                  )}
                </div>
              </div>
            )}

            {/* Reporting Structure */}
            {relationshipMap.reporting_structure && (
              <div className="grid grid-cols-2 gap-2">
                {relationshipMap.reporting_structure.likely_reports_to && (
                  <div className={TILE}>
                    <p className={EYEBROW} style={eyebrowStyle}>Reports To</p>
                    <p className="text-foreground dark:text-[#f7f8f8] mt-0.5" style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}>{relationshipMap.reporting_structure.likely_reports_to}</p>
                  </div>
                )}
                {relationshipMap.reporting_structure.likely_direct_reports != null && (
                  <div className={TILE}>
                    <p className={EYEBROW} style={eyebrowStyle}>Direct Reports</p>
                    <p className="text-foreground dark:text-[#f7f8f8] mt-0.5 tabular-nums" style={{ fontSize: '14px', fontWeight: 590, letterSpacing: '-0.13px' }}>{relationshipMap.reporting_structure.likely_direct_reports}</p>
                  </div>
                )}
              </div>
            )}

            {/* Other Contacts */}
            {relationshipMap.other_contacts_at_company && relationshipMap.other_contacts_at_company.length > 0 && (
              <div>
                <span className={EYEBROW} style={eyebrowStyle}>Other Contacts at Company</span>
                <div className="mt-2 space-y-1.5">
                  {relationshipMap.other_contacts_at_company.filter((x: any) => x && typeof x === 'object').map((rc: any, idx: number) => (
                    <div key={idx} className="flex items-center justify-between bg-muted/40 dark:bg-white/[0.02] rounded-md p-2.5 border border-border/50 dark:border-white/[0.06]">
                      <div className="flex items-center gap-2.5">
                        <div className="h-7 w-7 rounded-full bg-[#5e6ad2]/[0.10] dark:bg-[#5e6ad2]/[0.14] border border-[#5e6ad2]/20 dark:border-[#5e6ad2]/25 flex items-center justify-center flex-shrink-0">
                          <User className="h-3.5 w-3.5 text-[#5e6ad2] dark:text-[#a5b4fc]" />
                        </div>
                        <div>
                          <span className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}>{rc.name}</span>
                          {rc.job_title && <span className="text-muted-foreground dark:text-[#8a8f98] ml-2" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>{rc.job_title}</span>}
                        </div>
                      </div>
                      {rc.relationship && (
                        <span className="px-2 py-0.5 rounded-full border bg-[#5e6ad2]/[0.10] text-[#5e6ad2] border-[#5e6ad2]/25 dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc] dark:border-[#5e6ad2]/30" style={{ fontSize: '10px', fontWeight: 510 }}>
                          {rc.relationship}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Provider Results */}
      {activeProviderPanels.map(provider => (
        <div key={provider.id} className={SECTION_CARD}>
          <div className={SECTION_HEADER}>
            {provider.id === 'financials' ? <CurrencyDollar className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc]" /> : <Newspaper className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc]" />}
            <span className="text-foreground dark:text-[#f7f8f8]" style={sectionTitleStyle}>{provider.label}</span>
          </div>
          <div className="p-5">
            {provider.panel}
          </div>
        </div>
      ))}

      {/* Custom AI */}
      {customAiEntries.length > 0 && customAiEntries.map(([slug, recipeResult]) => {
        const isExpanded = expandedCustomAi[slug] || false;
        return (
          <div key={slug} className={SECTION_CARD}>
            <button
              type="button"
              onClick={() => setExpandedCustomAi(prev => ({ ...prev, [slug]: !prev[slug] }))}
              className={cn(SECTION_HEADER, 'w-full hover:bg-muted/60 dark:hover:bg-white/[0.04] transition-colors duration-150')}
            >
              <Sparkle className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="fill" />
              <span className="text-foreground dark:text-[#f7f8f8] flex-1 text-left" style={sectionTitleStyle}>
                Custom AI{recipeResult.recipe_name ? ` — ${recipeResult.recipe_name}` : ''}
              </span>
              <CaretDown className={cn('h-4 w-4 text-muted-foreground dark:text-[#8a8f98] transition-transform duration-200', isExpanded && 'rotate-180')} weight="bold" />
            </button>

            {!isExpanded && recipeResult.status !== 'failed' && (
              <div className="px-5 py-3 space-y-1.5">
                {recipeResult.output_mode === 'fields' && recipeResult.fields ? (
                  Object.entries(recipeResult.fields).map(([key, value]: [string, any]) => (
                    <div key={key} className="flex items-center gap-3" style={{ fontSize: '13px', letterSpacing: '-0.13px' }}>
                      <span className="text-muted-foreground dark:text-[#8a8f98] min-w-[100px]" style={{ fontSize: '11px', fontWeight: 510 }}>{key}</span>
                      <span className="text-foreground dark:text-[#f7f8f8] truncate">{typeof value === 'string' ? (value.length > 80 ? value.substring(0, 80) + '...' : value) : JSON.stringify(value)}</span>
                    </div>
                  ))
                ) : recipeResult.response ? (
                  <p className="text-foreground dark:text-[#f7f8f8] truncate" style={{ fontSize: '13px', letterSpacing: '-0.13px' }}>{recipeResult.response.substring(0, 120)}...</p>
                ) : (
                  <p className="text-muted-foreground dark:text-[#8a8f98] italic" style={{ fontSize: '13px', letterSpacing: '-0.13px' }}>No results yet</p>
                )}
              </div>
            )}

            {isExpanded && recipeResult.status !== 'failed' && (
              <div className="px-5 py-4 space-y-3">
                {recipeResult.output_mode === 'fields' && recipeResult.fields ? (
                  <div className="space-y-2">
                    {Object.entries(recipeResult.fields).map(([key, value]: [string, any]) => (
                      <div key={key} className="border border-border/50 dark:border-white/[0.06] rounded-md p-3 bg-muted/40 dark:bg-white/[0.02]">
                        <div className="flex items-center gap-2 mb-1.5">
                          <span className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '12px', fontWeight: 590, letterSpacing: '-0.1px' }}>{key}</span>
                          <span className="px-1.5 py-0.5 rounded-md border bg-[#5e6ad2]/[0.10] text-[#5e6ad2] border-[#5e6ad2]/25 dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc] dark:border-[#5e6ad2]/30" style={{ fontSize: '9px', fontWeight: 590 }}>
                            {typeof value === 'string' ? 'text' : typeof value === 'number' ? 'number' : typeof value === 'boolean' ? 'boolean' : 'object'}
                          </span>
                        </div>
                        <p className="text-foreground dark:text-[#d0d6e0] whitespace-pre-wrap" style={{ fontSize: '13px', letterSpacing: '-0.13px', lineHeight: 1.5 }}>{typeof value === 'string' ? value : JSON.stringify(value, null, 2)}</p>
                      </div>
                    ))}
                  </div>
                ) : recipeResult.response ? (
                  <div className="text-foreground dark:text-[#d0d6e0] whitespace-pre-wrap bg-muted/40 dark:bg-white/[0.02] border border-border/50 dark:border-white/[0.06] rounded-md p-4" style={{ fontSize: '13px', letterSpacing: '-0.13px', lineHeight: 1.5 }}>
                    {recipeResult.response}
                  </div>
                ) : (
                  <p className="text-muted-foreground dark:text-[#8a8f98] italic" style={{ fontSize: '13px', letterSpacing: '-0.13px' }}>No results yet</p>
                )}

                {(recipeResult.model || recipeResult.completed_at) && (
                  <div className="flex items-center gap-4 text-muted-foreground dark:text-[#62666d] border-t border-border/50 dark:border-white/[0.06] pt-2" style={{ fontSize: '11px', letterSpacing: '-0.05px' }}>
                    {recipeResult.model && (
                      <span className="flex items-center gap-1">
                        <Brain className="h-3 w-3" />
                        {recipeResult.model}
                      </span>
                    )}
                    {recipeResult.completed_at && (
                      <span className="flex items-center gap-1 tabular-nums">
                        <ClockPhosphor className="h-3 w-3" />
                        {new Date(recipeResult.completed_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}

            {recipeResult.status === 'failed' && (
              <div className="px-5 py-3 flex items-start gap-2">
                <Warning className="h-4 w-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" weight="fill" />
                <div>
                  <p className="text-red-700 dark:text-red-300" style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}>Custom AI enrichment failed</p>
                  {recipeResult.error && <p className="text-red-600/80 dark:text-red-400/80 mt-0.5" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>{recipeResult.error}</p>}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Sources */}
      {metadata.sources && metadata.sources.length > 0 && (
        <div className={SECTION_CARD}>
          <div className={SECTION_HEADER}>
            <ArrowSquareOut className="h-4 w-4 text-muted-foreground dark:text-[#8a8f98]" />
            <span className="text-foreground dark:text-[#f7f8f8]" style={sectionTitleStyle}>Sources</span>
            <span className="ml-auto bg-muted/60 text-muted-foreground border border-border/50 dark:bg-white/[0.04] dark:text-[#8a8f98] dark:border-white/[0.06] px-2 py-0.5 rounded-full tabular-nums" style={{ fontSize: '10px', fontWeight: 590 }}>
              {metadata.sources.length}
            </span>
          </div>
          <div className="p-4 space-y-1 max-h-40 overflow-y-auto">
            {metadata.sources.map((source: string, idx: number) => (
              <a
                key={idx}
                href={source}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-[#5e6ad2] dark:text-[#a5b4fc] hover:bg-muted/40 dark:hover:bg-white/[0.03] px-2 py-1.5 rounded-md transition-colors truncate"
                style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
              >
                <ArrowSquareOut className="h-3 w-3 flex-shrink-0" />
                {source}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Main Component
// ---------------------------------------------------------------------------

const LEAD_STATUS_TONES: Record<string, string> = {
  qualified: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/25 dark:bg-emerald-500/[0.12] dark:text-emerald-400 dark:border-emerald-500/25',
  engaged: 'bg-[#7170ff]/10 text-[#7170ff] border-[#7170ff]/25 dark:bg-[#7170ff]/[0.14] dark:text-[#a5b4fc] dark:border-[#7170ff]/30',
  contacted: 'bg-[#5e6ad2]/10 text-[#5e6ad2] border-[#5e6ad2]/25 dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc] dark:border-[#5e6ad2]/30',
};

const EMAIL_STATUS_TONES: Record<string, string> = {
  active: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/25 dark:bg-emerald-500/[0.12] dark:text-emerald-400 dark:border-emerald-500/25',
  bounced: 'bg-red-500/10 text-red-600 border-red-500/25 dark:bg-red-500/[0.12] dark:text-red-400 dark:border-red-500/25',
};

const NEUTRAL_PILL =
  'bg-muted/60 text-muted-foreground border-border/60 dark:bg-white/[0.04] dark:text-[#8a8f98] dark:border-white/[0.06]';

function StatusPill({ kind, value }: { kind: 'lead' | 'email'; value: string | undefined }) {
  if (!value) return null;
  const tone = kind === 'lead' ? LEAD_STATUS_TONES[value] : EMAIL_STATUS_TONES[value];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full border capitalize',
        tone || (kind === 'email'
          ? 'bg-amber-500/10 text-amber-700 border-amber-500/25 dark:bg-amber-500/[0.12] dark:text-amber-400 dark:border-amber-500/25'
          : NEUTRAL_PILL)
      )}
      style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full',
        (tone || (kind === 'email' ? 'amber' : 'neutral')).includes('emerald') ? 'bg-emerald-500 dark:bg-emerald-400' :
        (tone || '').includes('7170ff') ? 'bg-[#7170ff] dark:bg-[#a5b4fc]' :
        (tone || '').includes('5e6ad2') ? 'bg-[#5e6ad2] dark:bg-[#a5b4fc]' :
        (tone || '').includes('red') ? 'bg-red-500 dark:bg-red-400' :
        (tone || '').includes('amber') || (kind === 'email' && !tone) ? 'bg-amber-500 dark:bg-amber-400' :
        'bg-muted-foreground/60'
      )} aria-hidden />
      {value}
    </span>
  );
}

function EngagementStat({ value, label }: { value: number | undefined; label: string }) {
  const v = value ?? 0;
  // A cell in the segmented engagement strip. The tile itself is the emphasis;
  // the number pops when non-zero and dims to muted at zero so active metrics
  // read at a glance (indigo-dominant — no per-metric semantic colors).
  return (
    <div className="px-3.5 py-1.5 text-center min-w-[54px]">
      <p
        className={cn(
          'tabular-nums',
          v > 0 ? 'text-[#5e6ad2] dark:text-[#a5b4fc]' : 'text-muted-foreground/50 dark:text-[#62666d]',
        )}
        style={{ fontSize: '16px', fontWeight: 590, letterSpacing: '-0.16px', lineHeight: 1.1 }}
      >
        {v}
      </p>
      <p
        className="text-muted-foreground dark:text-[#8a8f98] uppercase mt-0.5"
        style={{ fontSize: '10px', fontWeight: 510, letterSpacing: '0.06em' }}
      >
        {label}
      </p>
    </div>
  );
}

// Tab order lives here (not inline in the JSX) so the tablist's keyboard
// handler can derive activeIndex/count from the same source the buttons and
// panels render from — a duplicated literal would silently drift.
const TABS = [
  { id: 'research' as const, label: 'Research', Icon: BookOpenPhosphor },
  { id: 'qualify' as const, label: 'Qualify', Icon: TargetPhosphor },
  { id: 'reach-out' as const, label: 'Reach Out', Icon: Lightning },
  { id: 'emails' as const, label: 'Emails', Icon: Envelope },
  { id: 'activity' as const, label: 'Activity', Icon: ClockPhosphor },
];

type ContactTabId = (typeof TABS)[number]['id'];

export function ContactDetailModal({ contact, isOpen, onClose }: ContactDetailModalProps) {
  const { deleteContact } = useContactStore();
  // contacts.hard_delete is Owner/Admin/Manager only — hide Delete where the
  // API would 403 (UX-only; the backend is the enforcement point).
  const canDeleteContact = usePermission('contacts', 'hard_delete').allowed;
  // Inert for an Analyst; a Rep's "gated" scope opens the modal, which
  // files an approval request rather than exporting.
  const canExportContact = usePermission('contacts', 'bulk_export').allowed;
  // Analyst holds no contacts.create_edit cell (spec 4-E), so Edit opens a form
  // whose only outcome is a 403 -- hide the entry point (AB#22336).
  const { canWrite: canEditContact } = usePermission('contacts', 'create_edit');

  const [enrichmentData, setEnrichmentData] = useState<any>(contact.enrichment_data || null);
  const [enrichmentStatus, setEnrichmentStatus] = useState<string>(contact.enrichment_status || 'not_enriched');
  const [apolloEnriching, setApolloEnriching] = useState(false);
  const [allEmails, setAllEmails] = useState<any[]>(contact.research_data?.all_emails || []);
  const [hunterVerifying, setHunterVerifying] = useState(false);
  const [hunterVerification, setHunterVerification] = useState<any>(
    contact.research_data?.hunter_verification || null
  );
  const [hunterEnriching, setHunterEnriching] = useState(false);
  const [showEnrichCatalog, setShowEnrichCatalog] = useState(false);
  const [activeTab, setActiveTab] = useState<ContactTabId>('research');
  const [showBookMeeting, setShowBookMeeting] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [enrichmentResults, setEnrichmentResults] = useState<Record<string, any>>(
    contact.enrichment_results || {}
  );
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  // Live lead-score fields. Scoring runs in the background slightly AFTER
  // enrichment completes, so we re-fetch the contact (briefly polling) to
  // pick up the recomputed fit/intent/lead_score without a full page reload.
  const [liveScore, setLiveScore] = useState<{
    lead_score?: number; fit_score?: number; intent_score?: number; score_breakdown?: any;
  }>({
    lead_score: contact.lead_score,
    fit_score: contact.fit_score,
    intent_score: contact.intent_score,
    score_breakdown: contact.score_breakdown,
  });

  const refreshScore = async (attempts = 4, delayMs = 1500) => {
    for (let i = 0; i < attempts; i++) {
      await new Promise((r) => setTimeout(r, delayMs));
      try {
        const res = await api.get(`/contacts/${contact.id}`);
        const d = res.data || {};
        setLiveScore({
          lead_score: d.lead_score, fit_score: d.fit_score,
          intent_score: d.intent_score, score_breakdown: d.score_breakdown,
        });
        if (d.score_breakdown) break; // scored — stop polling
      } catch {
        // ignore — best-effort refresh
      }
    }
  };

  // Lock background scroll while drawer is open
  useScrollLock(isOpen);

  // ESC key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !showDeleteConfirm) onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose, showDeleteConfirm]);

  // Reset state when a new contact is opened
  useEffect(() => {
    setAllEmails(contact.research_data?.all_emails || []);
    setHunterVerification(contact.research_data?.hunter_verification || null);
    setEnrichmentData(contact.enrichment_data || null);
    setEnrichmentStatus(contact.enrichment_status || 'not_enriched');
    setEnrichmentResults(contact.enrichment_results || {});
    setLiveScore({
      lead_score: contact.lead_score, fit_score: contact.fit_score,
      intent_score: contact.intent_score, score_breakdown: contact.score_breakdown,
    });
  }, [contact.id]);

  const handleApolloEnrich = async () => {
    setApolloEnriching(true);
    try {
      const res = await integrationsApi.apolloEnrichContacts([contact.id]);
      const result = res.data.results?.[0];
      if (result?.status === 'enriched') {
        const emails = result.all_emails || [];
        setAllEmails(emails);
        toast.success(emails.length > 0 ? `Enriched. ${emails.length} email(s) found.` : 'Enriched.');
        useContactStore.getState().fetchContacts();
      } else {
        toast.error(explainEnrichmentError(result?.error, 'Apollo'));
      }
    } catch (error: any) {
      toast.error(explainEnrichmentError(error.response?.data?.detail, 'Apollo'));
    } finally {
      setApolloEnriching(false);
    }
  };

  const handleHunterVerify = async () => {
    setHunterVerifying(true);
    try {
      const res = await integrationsApi.hunterVerifyEmail({ email: contact.email });
      setHunterVerification(res.data);
      toast.success(`Hunter: Email is ${res.data.status} (score: ${res.data.score})`);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Hunter verification failed');
    } finally {
      setHunterVerifying(false);
    }
  };

  const handleHunterEnrich = async () => {
    setHunterEnriching(true);
    try {
      const res = await integrationsApi.hunterEnrichContacts([contact.id]);
      const result = res.data.results?.[0];
      if (result?.status === 'enriched') {
        if (result.verification) setHunterVerification(result.verification);
        toast.success('Hunter enriched successfully');
        useContactStore.getState().fetchContacts();
      } else {
        toast.error(explainEnrichmentError(result?.error, 'Hunter'));
      }
    } catch (error: any) {
      toast.error(explainEnrichmentError(error.response?.data?.detail, 'Hunter'));
    } finally {
      setHunterEnriching(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const outcome = await deleteContact(contact.id);
      if (outcome && outcome.status === 'pending_approval') {
        // A Representative's delete is a request for a manager to approve.
        toast.success('Sent for approval', { description: outcome.message });
      } else {
        toast.success('Contact deleted successfully');
      }
      setShowDeleteConfirm(false);
      onClose();
    } catch (error: any) {
      // deleteContact opts out of the interceptor toast (skipErrorToast), so
      // this component owns the presentation.
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 409 && typeof detail === 'string') {
        // Blocked by active campaigns: a warning (amber), not a failure (red).
        toast.warning('Contact can’t be deleted yet', {
          description: detail,
          duration: 8000,
        });
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Failed to delete contact');
      }
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  if (typeof document === 'undefined') return null;

  return createPortal((
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="contact-detail-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed top-0 left-0 w-screen h-screen z-[60] backdrop-blur-md"
            onClick={onClose}
            aria-hidden="true"
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="contact-detail-drawer"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 32, stiffness: 320 }}
            className={cn(
              'fixed inset-y-0 right-0 z-[70] flex flex-col',
              'w-full md:w-[85vw] lg:w-[80vw] xl:w-[70vw] 2xl:w-[60vw]',
              'bg-card border-l border-border/60 dark:bg-[#0f1011] dark:border-white/[0.08]',
              'dark:shadow-[-24px_0_64px_rgba(0,0,0,0.55),inset_1px_0_0_rgba(255,255,255,0.02)]'
            )}
          >
            {/* Header */}
            <div className="flex-shrink-0 border-b border-border/50 dark:border-white/[0.06]">
              {/* Identity row */}
              <div className="flex items-center justify-between px-6 pt-4 pb-3 gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-11 h-11 rounded-md bg-[#5e6ad2]/10 dark:bg-[#5e6ad2]/[0.14] flex items-center justify-center flex-shrink-0 border border-[#5e6ad2]/20 dark:border-[#5e6ad2]/25"
                    style={{ fontSize: '14px', fontWeight: 590, letterSpacing: '-0.13px' }}>
                    <span className="text-[#5e6ad2] dark:text-[#a5b4fc]">
                      {getInitials(contact.full_name || contact.email)}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <h2
                      className="text-foreground dark:text-[#f7f8f8] truncate"
                      style={{ fontSize: '17px', fontWeight: 590, letterSpacing: '-0.182px', lineHeight: 1.3 }}
                    >
                      {contact.full_name || contact.email}
                    </h2>
                    <p
                      className="text-muted-foreground dark:text-[#8a8f98] truncate mt-0.5"
                      style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                    >
                      {contact.email}
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.05] transition-colors duration-150 flex-shrink-0"
                  aria-label="Close"
                >
                  <XPhosphor className="h-4 w-4" />
                </button>
              </div>

              {/* Status badges + engagement stats */}
              <div className="flex items-center justify-between px-6 pb-3 gap-4 flex-wrap">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <StatusPill kind="lead" value={contact.lead_status} />
                  <StatusPill kind="email" value={contact.email_status} />
                  {contact.lead_score != null && (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border bg-[#5e6ad2]/10 text-[#5e6ad2] border-[#5e6ad2]/25 dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc] dark:border-[#5e6ad2]/30"
                      style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
                    >
                      Score: <span className="tabular-nums" style={{ fontWeight: 590 }}>{contact.lead_score}</span>
                    </span>
                  )}
                </div>

                {/* Engagement stats — segmented metric strip so each number
                    reads as a deliberate, highlighted KPI rather than loose text. */}
                <div className="flex items-stretch rounded-lg border border-border/60 bg-muted/30 divide-x divide-border/50 overflow-hidden dark:border-white/[0.08] dark:bg-white/[0.02] dark:divide-white/[0.06]">
                  <EngagementStat value={contact.total_emails_sent} label="Sent" />
                  <EngagementStat value={contact.total_emails_opened} label="Opened" />
                  <EngagementStat value={contact.total_emails_clicked} label="Clicked" />
                  <EngagementStat value={contact.total_emails_replied} label="Replied" />
                </div>
              </div>
            </div>

            {/* Split Panel Content */}
            <div className="flex-1 flex min-h-0">
              {/* Left Panel -- Details + Verification */}
              <div className="w-[320px] flex-shrink-0 border-r border-border/50 dark:border-white/[0.06] overflow-y-auto p-5 bg-muted/20 dark:bg-white/[0.015]">
                <DetailsPanel
                  contact={{ ...contact, ...liveScore }}
                  allEmails={allEmails}
                  apolloEnriching={apolloEnriching}
                  onApolloEnrich={handleApolloEnrich}
                  hunterVerification={hunterVerification}
                  hunterVerifying={hunterVerifying}
                  hunterEnriching={hunterEnriching}
                  onHunterVerify={handleHunterVerify}
                  onHunterEnrich={handleHunterEnrich}
                />
              </div>

              {/* Right Panel -- Tabbed Enrichment View */}
              <div className="flex-1 flex flex-col min-h-0">
                {/* Enrichment controls -- always visible */}
                <div className="flex-shrink-0 px-6 pt-4 pb-2">
                  <EnrichmentPanel
                    contactId={contact.id}
                    enrichmentData={enrichmentData}
                    enrichmentStatus={enrichmentStatus}
                    onEnrichmentComplete={(data) => {
                      setEnrichmentData(data);
                      setEnrichmentStatus('completed');
                      refreshScore(); // pick up the recomputed Fit shortly after
                    }}
                    enrichmentResults={enrichmentResults}
                    onOpenCatalog={() => setShowEnrichCatalog(true)}
                    undeliverable={isUndeliverableContact(contact)}
                  />
                </div>

                {/* Tab bar */}
                <div className="flex-shrink-0 border-b border-border/50 dark:border-white/[0.06] px-6 flex items-center justify-between">
                  {/* role="tablist" sits on this wrapper only — the Book
                      Meeting button is a sibling of the tablist, not a tab. */}
                  <div
                    className="flex gap-1"
                    role="tablist"
                    aria-label="Contact detail sections"
                    onKeyDown={(e) =>
                      handleTabListKeyDown(e, {
                        activeIndex: TABS.findIndex(t => t.id === activeTab),
                        count: TABS.length,
                        onSelect: (i) => setActiveTab(TABS[i].id),
                      })
                    }
                  >
                    {TABS.map(tab => (
                      <button
                        key={tab.id}
                        role="tab"
                        id={`contact-tab-${tab.id}`}
                        aria-selected={activeTab === tab.id}
                        aria-controls={`contact-panel-${tab.id}`}
                        tabIndex={activeTab === tab.id ? 0 : -1}
                        onClick={() => setActiveTab(tab.id)}
                        className={cn(
                          'inline-flex items-center gap-1.5 px-3 py-2.5 border-b-2 transition-colors duration-150 whitespace-nowrap -mb-px',
                          activeTab === tab.id
                            ? 'border-[#5e6ad2] text-[#5e6ad2] dark:border-[#7170ff] dark:text-[#a5b4fc]'
                            : 'border-transparent text-muted-foreground hover:text-foreground dark:text-[#8a8f98] dark:hover:text-[#f7f8f8]'
                        )}
                        style={{ fontSize: '12px', fontWeight: activeTab === tab.id ? 590 : 510, letterSpacing: '-0.13px' }}
                      >
                        <tab.Icon className="h-3.5 w-3.5" weight={activeTab === tab.id ? 'bold' : 'regular'} />
                        {tab.label}
                      </button>
                    ))}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    icon={<CalendarPlus className="h-3.5 w-3.5" />}
                    onClick={() => setShowBookMeeting(true)}
                    disabled={isUndeliverableContact(contact)}
                    title={isUndeliverableContact(contact)
                      ? "This contact's email is flagged invalid; a meeting invite can't be delivered."
                      : undefined}
                  >
                    Book Meeting
                  </Button>
                </div>

                {/* Tab content — already the scroll container, so it carries
                    the tabpanel role (and tabIndex so keyboard users can
                    scroll it) instead of a redundant per-panel wrapper. Only
                    the active panel is mounted, so the ids track activeTab. */}
                <div
                  className="flex-1 overflow-y-auto p-6"
                  role="tabpanel"
                  id={`contact-panel-${activeTab}`}
                  aria-labelledby={`contact-tab-${activeTab}`}
                  tabIndex={0}
                >
                  {activeTab === 'research' && <ResearchTab contact={contact} enrichmentData={enrichmentData} enrichmentResults={enrichmentResults || {}} />}
                  {activeTab === 'qualify' && <QualifyTab contact={contact} enrichmentData={enrichmentData} enrichmentResults={enrichmentResults || {}} />}
                  {activeTab === 'reach-out' && <ReachOutTab contact={contact} enrichmentData={enrichmentData} enrichmentResults={enrichmentResults || {}} />}
                  {activeTab === 'emails' && <EmailsTab contact={contact} enrichmentData={enrichmentData} enrichmentResults={enrichmentResults || {}} />}
                  {activeTab === 'activity' && <ActivityTab contact={contact} enrichmentData={enrichmentData} enrichmentResults={enrichmentResults || {}} />}
                </div>
              </div>

              {/* Book Meeting Modal */}
              <BookMeetingModal
                isOpen={showBookMeeting}
                onClose={() => setShowBookMeeting(false)}
                contact={{ id: contact.id, name: contact.full_name || contact.email, email: contact.email }}
              />

              {/* Enrichment Catalog Modal */}
              <EnrichmentCatalogModal
                isOpen={showEnrichCatalog}
                onClose={() => setShowEnrichCatalog(false)}
                contactIds={[contact.id]}
                mode="single"
                onEnrichmentStarted={() => {
                  setEnrichmentStatus('in_progress');
                }}
                onEnrichmentComplete={({ enrichment_data, enrichment_results: providerResults }) => {
                  // Merge new provider results into local enrichment_results state
                  setEnrichmentResults((prev) => ({ ...prev, ...providerResults }));
                  // Update enrichment data
                  if (enrichment_data) {
                    setEnrichmentData(enrichment_data);
                    setEnrichmentStatus('completed');
                  }
                  // Refresh contact store + pick up the recomputed score
                  useContactStore.getState().fetchContacts();
                  refreshScore();
                }}
              />
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 flex items-center justify-between gap-3 px-6 py-3.5 border-t border-border/50 dark:border-white/[0.06] bg-muted/20 dark:bg-white/[0.015]">
              <div className="flex items-center gap-2">
                {canDeleteContact && (
                  <Button
                    variant="destructive"
                    size="sm"
                    icon={<Trash className="h-4 w-4" weight="regular" />}
                    onClick={() => setShowDeleteConfirm(true)}
                  >
                    Delete
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!canExportContact}
                  onClick={() => setShowExportModal(true)}
                  aria-label="Export to CRM"
                  title={canExportContact ? 'Export to CRM' : 'Your role cannot export contacts to a CRM'}
                >
                  <Export className="h-4 w-4" weight="regular" />
                </Button>
              </div>
              <div className="flex items-center gap-2.5">
                <Button variant="outline" size="sm" onClick={onClose}>
                  Close
                </Button>
                {canEditContact && (
                  <Button variant="default" size="sm" icon={<PencilSimple className="h-4 w-4" weight="bold" />} onClick={() => setShowEditModal(true)}>
                    Edit
                  </Button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Contact Modal — outside AnimatePresence so its mount/unmount
          does not re-trigger the drawer spring (root cause of right-side
          jitter on Delete press). */}
      <AddContactModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          useContactStore.getState().fetchContacts();
        }}
        editContact={contact}
      />

      {/* Delete confirm — same reasoning as above */}
      <ConfirmDialog
        open={showDeleteConfirm}
        title="Delete contact"
        description={'This contact will be permanently removed from your Contacts section. This action cannot be undone. To only take it out of a list or tag instead, use "Remove from List" or "Remove from Tag".'}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        tone="destructive"
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />

      {/* Export this contact to a connected CRM */}
      <CrmExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        contactIds={[contact.id]}
        contactCount={1}
        sourceName={contact.full_name || contact.email}
      />
    </>
  ), document.body);
}
